import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation, useParams } from 'react-router-dom';
import io from 'socket.io-client';
import { getApps, initializeApp } from 'firebase/app';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
  onMessage,
} from 'firebase/messaging';
import './QrForm.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';
const socketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'https://vakra.co.in';
const baseUrl = process.env.REACT_APP_BASE_URL || 'https://vakra.co.in';

const customerFirebaseConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    'AIzaSyBHc0bM8FIeV14aAfH8mC_SIlQXefuBE8k',
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    'vakra-orders-received.firebaseapp.com',
  projectId:
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'vakra-orders-received',
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    'vakra-orders-received.firebasestorage.app',
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ||
    '56991820937',
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    '1:56991820937:web:4f470dab790d9eeecf6a58',
};

const customerFirebaseVapidKey =
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BFAxG2lB2u-xqJL185SSyvdC7aZcf2M9sDniFAn4WZ0QrRHPtAuU8FtYkkc_CzPhwtXRsx7DqqZnX45DzWAQY88';

const CUSTOMER_FIREBASE_APP_NAME = 'vakra-customer-push';
const CUSTOMER_SERVICE_WORKER_PATH =
  '/firebase-customer-messaging-sw.js';
const CUSTOMER_SERVICE_WORKER_SCOPE =
  '/food-radar-push/';

const hasCustomerFirebaseConfig = () =>
  Boolean(
    customerFirebaseConfig.apiKey &&
      customerFirebaseConfig.projectId &&
      customerFirebaseConfig.messagingSenderId &&
      customerFirebaseConfig.appId &&
      customerFirebaseVapidKey
  );

const getCustomerFirebaseApp = () => {
  const existingApp = getApps().find(
    (app) => app.name === CUSTOMER_FIREBASE_APP_NAME
  );

  if (existingApp) return existingApp;

  return initializeApp(
    customerFirebaseConfig,
    CUSTOMER_FIREBASE_APP_NAME
  );
};

const getCustomerPushDeviceKey = () => {
  const storageKey = 'foodRadarPushDeviceKey';
  let deviceKey = localStorage.getItem(storageKey);

  if (deviceKey) return deviceKey;

  const randomPart =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random()
          .toString(36)
          .slice(2)}`;

  deviceKey = `web_${randomPart}`.slice(0, 191);

  localStorage.setItem(storageKey, deviceKey);
  return deviceKey;
};

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/400x400?text=No+Image';

const STEP_LANDING = 1;
const STEP_MODE = 2;
const STEP_CUSTOMER = 3;
const STEP_PRODUCTS = 4;
const STEP_REVIEW = 5;
const STEP_PAYMENT = 6;
const STEP_SUCCESS = 7;
const STEP_TRACK = 8;

const TRACKING_STEPS = [
  { key: 'pending', label: 'Order Placed', shortLabel: 'Placed', help: 'Order shop ko mil gaya' },
  { key: 'accepted', label: 'Accepted', shortLabel: 'Accepted', help: 'Shop ne order accept kiya' },
  { key: 'preparing', label: 'Preparing', shortLabel: 'Preparing', help: 'Food prepare ho raha hai' },
  { key: 'ready', label: 'Ready', shortLabel: 'Ready', help: 'Order ready hai' },
  { key: 'done', label: 'Completed', shortLabel: 'Done', help: 'Order complete ho gaya' },
];

const roundDisplayAmount = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 0;

  const sign = num < 0 ? -1 : 1;
  const absolute = Math.abs(num);
  const floorValue = Math.floor(absolute);
  const decimalPart = absolute - floorValue;

  return sign * (decimalPart > 0.3 ? floorValue + 1 : floorValue);
};

const formatMoney = (value) => `₹${roundDisplayAmount(value).toLocaleString('en-IN')}`;

const normalizeMobile = (value) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, 10);

const getFoodRadarCustomerId = (customer = null) => {
  const rawId =
    customer?.food_radar_customer_id ??
    customer?.foodRadarCustomerId ??
    customer?.customer_id ??
    customer?.customerId ??
    customer?.id ??
    localStorage.getItem('foodRadarCustomerId') ??
    0;

  const id = Number(rawId || 0);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeFlag = (value, fallback = 0) => {
  if (value === true) return 1;
  if (value === false) return 0;
  if (value === 1 || value === 0) return Number(value);

  const text = String(value ?? '').trim().toLowerCase();

  if (['1', 'true', 'yes', 'on', 'enabled', 'active'].includes(text)) return 1;
  if (['0', 'false', 'no', 'off', 'disabled', 'inactive'].includes(text)) return 0;

  return fallback;
};

const safeNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeImage = (value) => {
  const img = Array.isArray(value) ? value[0] : value;

  if (!img) return PLACEHOLDER_IMAGE;

  if (String(img).startsWith('http://') || String(img).startsWith('https://')) {
    return img;
  }

  return `${baseUrl}${String(img).startsWith('/') ? '' : '/'}${img}`;
};

const parseShopTimeToMinutes = (value) => {
  if (!value) return null;

  const text = String(value).trim();
  if (!text) return null;

  const timeOnly = text.includes('T')
    ? text.split('T')[1]?.slice(0, 5)
    : text.slice(0, 5);

  const match = String(timeOnly || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return hour * 60 + minute;
};

const formatShopTime = (value) => {
  const minutes = parseShopTimeToMinutes(value);
  if (minutes == null) return '';

  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;

  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
};

const getShopTimingText = (settings = {}) => {
  const open = formatShopTime(settings.shop_open_time || settings.shopOpenTime);
  const close = formatShopTime(settings.shop_close_time || settings.shopCloseTime);

  if (open && close) return `${open} - ${close}`;
  if (open) return `Open: ${open}`;
  if (close) return `Close: ${close}`;

  return 'Not set';
};

const getShopOpenStatus = (settings = {}) => {
  const openMinutes = parseShopTimeToMinutes(settings.shop_open_time || settings.shopOpenTime);
  const closeMinutes = parseShopTimeToMinutes(settings.shop_close_time || settings.shopCloseTime);

  if (openMinutes == null || closeMinutes == null) {
    return {
      isOpen: true,
      label: 'Timing Not Set',
      message: 'Shop timing set nahi hai, isliye order allowed hai.',
    };
  }

  if (openMinutes === closeMinutes) {
    return {
      isOpen: true,
      label: 'Open 24 Hours',
      message: 'Shop 24 hours open hai.',
    };
  }

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  let isOpen = false;

  if (openMinutes < closeMinutes) {
    isOpen = nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
  } else {
    isOpen = nowMinutes >= openMinutes || nowMinutes <= closeMinutes;
  }

  return {
    isOpen,
    label: isOpen ? 'Open Now' : 'Closed Now',
    message: isOpen
      ? `Shop open hai. Timing: ${getShopTimingText(settings)}`
      : `Shop abhi closed hai. Order ${getShopTimingText(settings)} ke time par place kar sakte hain.`,
  };
};

const normalizeOrderSettings = (raw = {}) => {
  const data = raw?.settings || raw?.data || raw || {};

  return {
    enable_dine_in: safeFlag(data.enable_dine_in, 1),
    enable_pickup: safeFlag(data.enable_pickup ?? data.enable_takeaway, 1),
    enable_home_delivery: safeFlag(data.enable_home_delivery ?? data.enable_delivery, 0),

    allow_cod: safeFlag(data.allow_cod, 1),
    allow_online_payment: safeFlag(data.allow_online_payment, 1),

    min_order_amount: safeNumber(data.min_order_amount ?? data.minimum_order_amount ?? data.minOrderAmount ?? data.minimumOrderAmount, 0),
    dine_in_min_amount: safeNumber(data.dine_in_min_amount ?? data.dineInMinAmount ?? data.dine_in_min_order_amount, 0),
    pickup_min_amount: safeNumber(data.pickup_min_amount ?? data.takeaway_min_amount ?? data.pickupMinAmount ?? data.takeawayMinAmount ?? data.pickup_min_order_amount, 0),
    delivery_min_amount: safeNumber(data.delivery_min_amount ?? data.deliveryMinAmount ?? data.delivery_min_order_amount, 0),

    delivery_fee: safeNumber(data.delivery_fee, 0),
    packaging_fee: safeNumber(data.packaging_fee, 0),
    delivery_radius_km: safeNumber(data.delivery_radius_km, 0),
    delivery_eta_min: safeNumber(data.delivery_eta_min, 30),
    delivery_eta_max: safeNumber(data.delivery_eta_max, 60),

    shop_open_time: data.shop_open_time || data.shopOpenTime || data.opening_time || '',
    shop_close_time: data.shop_close_time || data.shopCloseTime || data.closing_time || '',
  };
};

const safeParseProducts = (value) => {
  try {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') return JSON.parse(value);
    return [];
  } catch {
    return [];
  }
};

const normalizeStatusText = (value) => {
  const text = String(value || '').trim();
  if (!text) return '-';

  return text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeTrackingStatus = (value) => {
  const status = String(value || '').toLowerCase().trim();

  if (!status) return 'pending';
  if (status === 'packing') return 'preparing';
  if (status === 'packed') return 'preparing';
  if (status === 'complete') return 'done';
  if (status === 'completed') return 'done';
  if (status === 'delivered') return 'done';
  if (status === 'cancel') return 'cancelled';
  if (status === 'canceled') return 'cancelled';

  if (['pending', 'accepted', 'preparing', 'ready', 'done', 'cancelled'].includes(status)) {
    return status;
  }

  return 'pending';
};

const getLatestTrackingStatus = (order) => {
  const logs = Array.isArray(order?.logs) ? order.logs : [];

  if (logs.length > 0) {
    const latestLog = logs[logs.length - 1];

    return normalizeTrackingStatus(
      latestLog.new_status ||
        latestLog.status ||
        latestLog.displayStatus ||
        order?.status
    );
  }

  return normalizeTrackingStatus(order?.status);
};

const getTrackingIndex = (status) => {
  const safeStatus = normalizeTrackingStatus(status);

  if (safeStatus === 'cancelled') return -1;

  const foundIndex = TRACKING_STEPS.findIndex((stepItem) => stepItem.key === safeStatus);
  return foundIndex >= 0 ? foundIndex : 0;
};

const buildDisplayLogs = (order) => {
  const logs = Array.isArray(order?.logs) ? order.logs : [];

  if (logs.length > 0) {
    return logs.map((log, index) => ({
      id: log.id || `${log.new_status || log.status || 'log'}-${index}`,
      status: normalizeTrackingStatus(log.new_status || log.status),
      label: normalizeStatusText(log.new_status || log.status),
      note: log.note || '',
      created_at: log.created_at || log.createdAt || '',
    }));
  }

  return [
    {
      id: 'fallback-pending',
      status: normalizeTrackingStatus(order?.status || 'pending'),
      label: normalizeStatusText(order?.status || 'pending'),
      note: 'Order placed',
      created_at: order?.created_at || order?.createdAt || '',
    },
  ];
};

const getOrderEtaState = (order, nowMs = Date.now()) => {
  const status = getLatestTrackingStatus(order);

  if (!['accepted', 'preparing'].includes(status)) return null;

  const etaMinutes = Math.max(
    0,
    Number(order?.delivery_eta_minutes || order?.deliveryEtaMinutes || 0)
  );

  if (!etaMinutes) return null;

  const directDeadline = Number(
    order?.eta_deadline_unix_ms || order?.etaDeadlineUnixMs || 0
  );
  const acceptedUnixMs = Number(
    order?.accepted_at_unix_ms || order?.acceptedAtUnixMs || 0
  );

  let deadlineMs = directDeadline;

  if (!deadlineMs && acceptedUnixMs) {
    deadlineMs = acceptedUnixMs + etaMinutes * 60 * 1000;
  }

  if (!deadlineMs) {
    const acceptedAt = order?.accepted_at || order?.acceptedAt;
    const parsedAcceptedAt = acceptedAt ? new Date(acceptedAt).getTime() : 0;
    if (Number.isFinite(parsedAcceptedAt) && parsedAcceptedAt > 0) {
      deadlineMs = parsedAcceptedAt + etaMinutes * 60 * 1000;
    }
  }

  if (!deadlineMs) return null;

  const remainingMs = deadlineMs - nowMs;
  const remainingMinutes = Math.max(0, Math.ceil(remainingMs / 60000));
  const isDelivery = String(order?.order_mode || order?.orderMode || '').toLowerCase() === 'delivery';

  return {
    deadlineMs,
    remainingMs,
    remainingMinutes,
    expired: remainingMs <= 0,
    label: remainingMs <= 0
      ? 'Expected any moment'
      : `${remainingMinutes} minute baki`,
    help: isDelivery
      ? 'Aapka order estimated time me deliver hoga.'
      : 'Aapka order estimated time me ready hoga.',
  };
};

const getFoodBadge = (foodType) => {
  const type = String(foodType || 'veg').toLowerCase();

  if (type === 'non_veg' || type === 'nonveg' || type === 'non-veg') {
    return { label: '🔴 Non-Veg', className: 'nonveg' };
  }

  if (type === 'egg') {
    return { label: '🟡 Egg', className: 'egg' };
  }

  return { label: '🟢 Veg', className: 'veg' };
};

const getShortDescription = (description, words = 8) => {
  const text = String(description || '').trim();
  if (!text) return '';

  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length <= words) return text;

  return `${parts.slice(0, words).join(' ')}...`;
};

const shouldShowReadMore = (description, words = 8) => {
  return String(description || '').trim().split(/\s+/).filter(Boolean).length > words;
};

const parseJsonMaybe = (value, fallback = null) => {
  try {
    if (value === null || value === undefined || value === '') return fallback;
    if (Array.isArray(value) || typeof value === 'object') return value;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const normalizeOffer = (offer = {}) => {
  const offerJson = parseJsonMaybe(offer.offer_json || offer.offerJson || offer.metadata, {}) || {};
  const comboItemsRaw = offer.combo_items || offer.comboItems || offerJson.combo_items || [];
  const comboItems = parseJsonMaybe(comboItemsRaw, []);

  const offerType = String(
    offer.offer_type ||
      offer.offerType ||
      offerJson.offer_type ||
      'bill_discount'
  ).trim().toLowerCase();

  const isActive = safeFlag(offer.is_active ?? offer.isActive ?? offerJson.is_active ?? 1, 1) === 1;

  return {
    ...offer,
    ...offerJson,
    id: Number(offer.id || offer.offerId || offerJson.id || 0),
    offerId: Number(offer.id || offer.offerId || offerJson.id || 0),
    offer_type: offerType,
    offer_name:
      offer.offer_name ||
      offer.offerName ||
      offer.combo_name ||
      offerJson.offer_name ||
      offer.offer_text ||
      'Offer',
    offer_text: offer.offer_text || offer.offerText || offerJson.offer_text || '',
    combo_items: Array.isArray(comboItems) ? comboItems : [],
    progressive_steps: safeArray(
      parseJsonMaybe(
        offer.progressive_steps || offer.progressiveSteps || offerJson.progressive_steps,
        []
      )
    ),
    buy_product_id: Number(offer.buy_product_id || offer.buyProductId || offerJson.buy_product_id || 0),
    buy_quantity: Math.max(1, Number(offer.buy_quantity || offer.buyQuantity || offerJson.buy_quantity || 1)),
    get_product_id: Number(offer.get_product_id || offer.getProductId || offerJson.get_product_id || 0),
    get_quantity: Math.max(1, Number(offer.get_quantity || offer.getQuantity || offerJson.get_quantity || 1)),
    reward_type: offer.reward_type || offer.rewardType || offerJson.reward_type || 'free',
    reward_value: safeNumber(offer.reward_value ?? offer.rewardValue ?? offerJson.reward_value, 0),
    start_time: offer.start_time || offer.startTime || offerJson.start_time || '',
    end_time: offer.end_time || offer.endTime || offerJson.end_time || '',
    days_active: safeArray(parseJsonMaybe(offer.days_active || offer.daysActive || offerJson.days_active, [])),
    combo_price: safeNumber(offer.combo_price ?? offer.comboPrice ?? offerJson.combo_price, 0),
    actual_price: safeNumber(offer.actual_price ?? offer.actualPrice ?? offerJson.actual_price, 0),
    customer_save: safeNumber(offer.customer_save ?? offer.customerSave ?? offerJson.customer_save, 0),
    addon_product_id: Number(offer.addon_product_id || offer.addonProductId || offerJson.addon_product_id || 0),
    addon_product_name: offer.addon_product_name || offer.addonProductName || offerJson.addon_product_name || '',
    addon_original_price: safeNumber(offer.addon_original_price ?? offer.addonOriginalPrice ?? offerJson.addon_original_price, 0),
    addon_special_price: safeNumber(offer.addon_special_price ?? offer.addonSpecialPrice ?? offerJson.addon_special_price, 0),
    min_bill_amount: safeNumber(offer.min_bill_amount ?? offer.minBillAmount ?? offer.min_purchasing_price ?? offerJson.min_bill_amount, 0),
    min_purchasing_price: safeNumber(offer.min_purchasing_price ?? offer.min_bill_amount ?? offerJson.min_purchasing_price, 0),
    discount_type: offer.discount_type || offer.discountType || offerJson.discount_type || 'flat',
    discount_value: safeNumber(offer.discount_value ?? offer.discountValue ?? offerJson.discount_value, 0),
    max_discount: safeNumber(offer.max_discount ?? offer.maxDiscount ?? offerJson.max_discount, 0),
    limit_per_order: Math.max(1, Number(offer.limit_per_order || offer.limitPerOrder || offerJson.limit_per_order || 1)),
    is_active: isActive,
  };
};

const getOfferTypeLabel = (type) => {
  if (type === 'combo') return 'Combo Offer';
  if (type === 'bill_unlock_addon') return 'Bill Unlock Add-on';
  if (type === 'bill_discount') return 'Discount Offer';
  if (type === 'buy_x_get_y') return 'Buy X Get Y';
  if (type === 'time_slot') return 'Happy Hour';
  if (type === 'progressive_unlock') return 'Progressive Unlock';
  if (type === 'coin_reward') return 'Coin Reward';
  return 'Offer';
};

const parseCoordinate = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const isValidLatitude = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= -90 && num <= 90;
};

const isValidLongitude = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= -180 && num <= 180;
};

const isUsableCoordinatePair = (lat, lng) => {
  const finalLat = Number(lat);
  const finalLng = Number(lng);

  if (!isValidLatitude(finalLat) || !isValidLongitude(finalLng)) {
    return false;
  }

  // 0,0 fake/fallback coordinate hota hai. Mumbai se 0,0 approx 8210 KM dikhta hai.
  if (finalLat === 0 && finalLng === 0) {
    return false;
  }

  return true;
};

const getFirstUsableCoordinatePair = (...pairs) => {
  for (const pair of pairs) {
    if (!Array.isArray(pair) || pair.length < 2) continue;

    const lat = parseCoordinate(pair[0]);
    const lng = parseCoordinate(pair[1]);

    if (isUsableCoordinatePair(lat, lng)) {
      return { latitude: lat, longitude: lng };
    }
  }

  return { latitude: null, longitude: null };
};

const normalizePublicProfilePayload = (raw = {}) => {
  const payload =
    raw?.profile ||
    raw?.vendor ||
    raw?.user ||
    raw?.data ||
    raw ||
    {};

  const coords = getFirstUsableCoordinatePair(
    [payload.shop_latitude, payload.shop_longitude],
    [payload.shopLatitude, payload.shopLongitude],
    [payload.shop_lat, payload.shop_lng],
    [payload.shop_lat, payload.shop_lon],
    [payload.latitude, payload.longitude],
    [payload.lat, payload.lng],
    [payload.lat, payload.lon]
  );

  return {
    ...payload,
    shop_latitude: coords.latitude,
    shop_longitude: coords.longitude,
    shopLatitude: coords.latitude,
    shopLongitude: coords.longitude,
  };
};

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const fromLat = Number(lat1);
  const fromLon = Number(lon1);
  const toLat = Number(lat2);
  const toLon = Number(lon2);

  if (!isUsableCoordinatePair(fromLat, fromLon) || !isUsableCoordinatePair(toLat, toLon)) {
    console.warn('Invalid coordinate pair for distance calculation:', {
      fromLat,
      fromLon,
      toLat,
      toLon,
    });
    return null;
  }

  const toRad = (value) => (Number(value) * Math.PI) / 180;
  const earthRadiusKm = 6371;

  const dLat = toRad(toLat - fromLat);
  const dLon = toRad(toLon - fromLon);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(fromLat)) *
      Math.cos(toRad(toLat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
};

const getAddressPart = (address = {}, keys = []) => {
  for (const key of keys) {
    if (address[key]) return address[key];
  }

  return '';
};

const reverseGeocodeCustomerLocation = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const address = data.address || {};

    const house = getAddressPart(address, ['house_number', 'building', 'amenity']);
    const road = getAddressPart(address, ['road', 'neighbourhood', 'suburb']);
    const area = getAddressPart(address, [
      'suburb',
      'neighbourhood',
      'quarter',
      'village',
      'town',
      'city_district',
    ]);
    const finalCity = getAddressPart(address, ['city', 'town', 'village', 'municipality', 'county']);
    const finalState = address.state || '';
    const finalPincode = String(address.postcode || '').replace(/\D/g, '').slice(0, 6);

    return {
      displayName: data.display_name || '',
      addressLine1: [house, road].filter(Boolean).join(', '),
      addressLine2: area,
      city: finalCity,
      state: finalState,
      pincode: finalPincode,
    };
  } catch (error) {
    console.error('Reverse geocode failed:', error);
    return null;
  }
};

const StandardQrForm = () => {
  const location = useLocation();
  const routeParams = useParams();
  const queryParams = new URLSearchParams(location.search);

  // New secure QR URL support:
  // /qr-form/:qrToken
  // Old QR URL support:
  // /qr-form?vendorId=43&businessType=fastfood&table=1&qrName=T1
  const qrToken = String(
    routeParams.qrToken ||
      queryParams.get('qrToken') ||
      queryParams.get('qr_token') ||
      ''
  ).trim();

  const [resolvedQr, setResolvedQr] = useState(null);

  const queryQrName = String(queryParams.get('qrName') || '').trim();
  const resolvedQrName = String(resolvedQr?.qrName || resolvedQr?.qr_name || '').trim();

  const qrName = resolvedQrName || queryQrName;

  const qrType = String(
    resolvedQr?.qrType ||
      resolvedQr?.qr_type ||
      queryParams.get('qrType') ||
      queryParams.get('qr_type') ||
      ''
  ).toLowerCase().trim();

  const tableNumberFromQrName = /^T\d+$/i.test(qrName)
    ? qrName.slice(1)
    : '';

  const vendorId = String(
    resolvedQr?.vendorId ||
      resolvedQr?.vendor_id ||
      queryParams.get('vendorId') ||
      ''
  ).trim();

  const businessType = String(
    resolvedQr?.businessType ||
      resolvedQr?.business_type ||
      queryParams.get('businessType') ||
      'fastfood'
  ).toLowerCase().trim();

  const notificationTrackRequested = queryParams.get('track') === '1';
  const notificationTrackOrderId = Number(queryParams.get('orderId') || 0);

  const tableNumber = String(
    resolvedQr?.tableNumber ||
      resolvedQr?.table_number ||
      queryParams.get('table') ||
      queryParams.get('table_number') ||
      tableNumberFromQrName ||
      ''
  ).trim();

  const qrDisplayLabel = String(tableNumber || qrName || '').trim();

  const qrModeIdentity = `${qrType} ${qrName} ${tableNumber}`
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // QR mode priority:
  // Table QR -> Dine In, Parcel/Pickup QR -> Takeaway.
  // Normal QR ka default vendor settings ke hisab se neeche select hota hai.
  const isParcelQr =
    /(^|\s)(parcel|takeaway|take away|pickup|pick up)(\s|$)/i.test(qrModeIdentity);

  const hasValidTableNumber =
    Boolean(tableNumber) &&
    !['unknown', 'null', 'undefined'].includes(tableNumber.toLowerCase());

  const hasExplicitTableQrName =
    /^t\s*\d+$/i.test(qrName) ||
    /^table\s*[a-z0-9-]+$/i.test(qrName) ||
    /(^|\s)(table|dine in|dinein)(\s|$)/i.test(qrModeIdentity);

  const isTableQr =
    !isParcelQr && (hasValidTableNumber || hasExplicitTableQrName);

  const qrHasTable = isTableQr;

  const qrHasDisplayLocation =
    qrDisplayLabel &&
    qrDisplayLabel !== 'Unknown' &&
    qrDisplayLabel !== 'null' &&
    qrDisplayLabel !== 'undefined';

  const paymentDismissTimerRef = useRef(null);
  const socketRef = useRef(null);
  const lookupTimerRef = useRef(null);
  const deviceProfileLookupDoneRef = useRef(false);
  const searchShellRef = useRef(null);
  const customerFcmSyncKeyRef = useRef('');
  const customerFcmLastSyncAtRef = useRef(0);

  // QR scan ke turant baad customer ko sabse pehle menu dikhana hai.
  const [step, setStep] = useState(STEP_PRODUCTS);

  const [loading, setLoading] = useState(true);
  const [qrAccess, setQrAccess] = useState({
    checked: false,
    isActive: true,
    message: '',
  });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paidOnline, setPaidOnline] = useState(false);
  const [verifiedPaymentData, setVerifiedPaymentData] = useState(null);
  const [isPaymentVerifying, setIsPaymentVerifying] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');

  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [vendorProfile, setVendorProfile] = useState({
    shop_name: '',
    shopName: '',
    shop_image: '',
    shopImage: '',
    shop_logo: '',
    live_location: '',
    liveLocation: '',
    shop_latitude: null,
    shop_longitude: null,
    shopLatitude: null,
    shopLongitude: null,
    latitude: null,
    longitude: null,
    number: '',
    mobile: '',
    phone: '',
    contact: '',
  });

  const [allProducts, setAllProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [showOffersPanel, setShowOffersPanel] = useState(false);
  const [showCoinWalletPanel, setShowCoinWalletPanel] = useState(false);
  const [showProductFilters, setShowProductFilters] = useState(false);
  const [showBottomCategorySlider, setShowBottomCategorySlider] = useState(false);
  const [showFloatingSearchShortcut, setShowFloatingSearchShortcut] = useState(false);
  const [minOrderPopup, setMinOrderPopup] = useState(null);
  const [offerHintDismissedKey, setOfferHintDismissedKey] = useState('');
  const [orderSettings, setOrderSettings] = useState(normalizeOrderSettings({}));

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [foodTypeFilter, setFoodTypeFilter] = useState('');
  const [showTrendingOnly, setShowTrendingOnly] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const [cart, setCart] = useState([]);
  const [orderMode, setOrderMode] = useState(isTableQr ? 'dine_in' : 'takeaway');
  const [paymentMode, setPaymentMode] = useState('cash');

  const [mobileNumber, setMobileNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [landmark, setLandmark] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [pincode, setPincode] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [customerLatitude, setCustomerLatitude] = useState('');
  const [customerLongitude, setCustomerLongitude] = useState('');
  const [customerLocationAccuracy, setCustomerLocationAccuracy] = useState(null);
  const [customerLocationLoading, setCustomerLocationLoading] = useState(false);
  const [customerLocationMessage, setCustomerLocationMessage] = useState('');
  const [deliveryDistanceKm, setDeliveryDistanceKm] = useState(null);

  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [customerLookupMessage, setCustomerLookupMessage] = useState('');
  const [deviceProfileLoading, setDeviceProfileLoading] = useState(false);
  const [savedDeviceProfile, setSavedDeviceProfile] = useState(null);
  const [savedDeviceProfileApplied, setSavedDeviceProfileApplied] = useState(false);
  const [isEditingCustomerDetails, setIsEditingCustomerDetails] = useState(false);
  const [deviceProfileMessage, setDeviceProfileMessage] = useState('');
  const [foodRadarCustomerId, setFoodRadarCustomerId] = useState(() => {
    const saved = Number(localStorage.getItem('foodRadarCustomerId') || 0);
    return Number.isInteger(saved) && saved > 0 ? saved : null;
  });
  const [qrCoinWallet, setQrCoinWallet] = useState({
    availableCoins: 0,
    pendingCoins: 0,
  });
  const [qrCoinWalletLoading, setQrCoinWalletLoading] = useState(false);
  const [qrCoinWalletError, setQrCoinWalletError] = useState('');
  const [selectedSmartOfferId, setSelectedSmartOfferId] = useState(null);

  // QR coin UX: earn here, claim/use only inside Food Radar.
  const [showCoinWinPopup, setShowCoinWinPopup] = useState(false);
  const [showCoinClaimPopup, setShowCoinClaimPopup] = useState(false);
  const [wonCoins, setWonCoins] = useState(0);

  const [trackMobile, setTrackMobile] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);
  const [todayOrders, setTodayOrders] = useState([]);
  const [trackingNowMs, setTrackingNowMs] = useState(() => Date.now());

  const [pricingPreview, setPricingPreview] = useState({
    products: [],
    baseTotal: 0,
    deliveryCharge: 0,
    packagingCharge: 0,
    extraCharges: 0,
    discountAmount: 0,
    finalTotal: 0,
    validatedOfferName: null,
    appliedOffer: null,
    deliveryEtaMinutes: null,
  });
  const [pricingPreviewReady, setPricingPreviewReady] = useState(false);
  const [pricingPreviewError, setPricingPreviewError] = useState('');
  const pricingPreviewRequestRef = useRef(0);

  const [placedOrder, setPlacedOrder] = useState(null);
  const [contactProduct, setContactProduct] = useState(null);

  const [notificationPermission, setNotificationPermission] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission || 'default';
  });
  const [notificationTokenSaving, setNotificationTokenSaving] = useState(false);
  const [notificationTokenSaved, setNotificationTokenSaved] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showLandingNotificationPrompt, setShowLandingNotificationPrompt] = useState(false);

  const shopName =
    vendorProfile.shop_name ||
    vendorProfile.shopName ||
    vendorProfile.name ||
    'Fast Food Shop';

  const shopImage =
    vendorProfile.shop_logo ||
    vendorProfile.shop_image ||
    vendorProfile.shopImage ||
    '';

  const shopLocation =
    vendorProfile.live_location ||
    vendorProfile.liveLocation ||
    '';

  const vendorNumber =
    vendorProfile.number ||
    vendorProfile.mobile ||
    vendorProfile.phone ||
    vendorProfile.contact ||
    '';

  const shopCoordinatePair = useMemo(() => {
    return getFirstUsableCoordinatePair(
      [vendorProfile.shop_latitude, vendorProfile.shop_longitude],
      [vendorProfile.shopLatitude, vendorProfile.shopLongitude],
      [vendorProfile.shop_lat, vendorProfile.shop_lng],
      [vendorProfile.shop_lat, vendorProfile.shop_lon],
      [vendorProfile.latitude, vendorProfile.longitude],
      [vendorProfile.lat, vendorProfile.lng],
      [vendorProfile.lat, vendorProfile.lon]
    );
  }, [vendorProfile]);

  const shopLatitude = shopCoordinatePair.latitude;
  const shopLongitude = shopCoordinatePair.longitude;

  const deliveryRadiusKm = Number(orderSettings.delivery_radius_km || 0);

  const hasShopCoordinates = isUsableCoordinatePair(shopLatitude, shopLongitude);

  const hasCustomerCoordinates = isUsableCoordinatePair(
    customerLatitude,
    customerLongitude
  );

  console.log('DELIVERY LOCATION FINAL DEBUG:', {
    vendorProfile,
    shopLatitude,
    shopLongitude,
    customerLatitude,
    customerLongitude,
    hasShopCoordinates,
    hasCustomerCoordinates,
    deliveryRadiusKm,
  });

  const deliveryRangeStatus = useMemo(() => {
    if (orderMode !== 'delivery') {
      return {
        checked: false,
        allowed: true,
        distanceKm: null,
        message: '',
      };
    }

    if (!deliveryRadiusKm || deliveryRadiusKm <= 0) {
      return {
        checked: false,
        allowed: true,
        distanceKm: null,
        message: 'Shop ne delivery radius set nahi kiya hai.',
      };
    }

    if (!hasShopCoordinates) {
      return {
        checked: false,
        allowed: false,
        distanceKm: null,
        message: 'Shop location set nahi hai. Vendor se contact karein.',
      };
    }

    if (!hasCustomerCoordinates) {
      return {
        checked: false,
        allowed: false,
        distanceKm: null,
        message: `Delivery ke liye location allow karein. Shop ${deliveryRadiusKm} KM radius me delivery karta hai.`,
      };
    }

    const distance = calculateDistanceKm(
      shopLatitude,
      shopLongitude,
      customerLatitude,
      customerLongitude
    );

    if (distance === null) {
      return {
        checked: false,
        allowed: false,
        distanceKm: null,
        message: 'Distance calculate nahi ho paya. Location dobara allow karein.',
      };
    }

    const allowed = distance <= deliveryRadiusKm;

    return {
      checked: true,
      allowed,
      distanceKm: distance,
      message: allowed
        ? `Delivery available. Aap shop se approx ${distance.toFixed(2)} KM door hain.`
        : `Delivery available nahi hai. Aap approx ${distance.toFixed(
            2
          )} KM door hain, shop sirf ${deliveryRadiusKm} KM tak delivery karta hai.`,
    };
  }, [
    orderMode,
    deliveryRadiusKm,
    hasShopCoordinates,
    hasCustomerCoordinates,
    shopLatitude,
    shopLongitude,
    customerLatitude,
    customerLongitude,
  ]);

  const normalizedProducts = useMemo(() => {
    return allProducts.map((product) => {
      const image = product.image_url || product.image || product.parentImage || '';

      return {
        ...product,
        id: Number(product.id || product.productId),
        productId: Number(product.id || product.productId),
        name: product.name || product.product_name || 'Food Item',
        image_url: normalizeImage(image),
        category: product.category || 'Others',
        brand: product.brand || '',
        pack_size: product.pack_size || product.unit_type || '',
        unit_type: product.unit_type || '',
        description: product.description || '',
        food_type: product.food_type || product.foodType || 'veg',
        trend_item: Number(product.trend_item || 0),
        is_active: Number(product.is_active ?? 1),
        availability: product.availability || product.status || 'available',
        stock_quantity: product.stock_quantity == null ? 999999 : Number(product.stock_quantity || 0),
        selling_price: safeNumber(product.selling_price ?? product.price_show_to_customer ?? product.price, 0),
        price: safeNumber(product.price_show_to_customer ?? product.price ?? product.selling_price, 0),
        show_price: Number(product.show_price ?? 1),
        pricing_type: product.pricing_type || 'fixed',
      };
    });
  }, [allProducts]);

  const normalizedOffers = useMemo(() => {
    const supportedCustomerOfferTypes = [
      'combo',
      'bill_unlock_addon',
      'bill_discount',
      'buy_x_get_y',
      'time_slot',
      'progressive_unlock',
      'coin_reward',
    ];

    return safeArray(offers)
      .map((offer) => {
        try {
          return normalizeOffer(offer);
        } catch {
          return null;
        }
      })
      .filter(
        (offer) =>
          offer &&
          offer.is_active &&
          supportedCustomerOfferTypes.includes(offer.offer_type)
      );
  }, [offers]);

  const offersByType = useMemo(() => {
    const groups = {
      combo: [],
      bill_unlock_addon: [],
      bill_discount: [],
      buy_x_get_y: [],
      time_slot: [],
      progressive_unlock: [],
      coin_reward: [],
    };

    normalizedOffers.forEach((offer) => {
      if (groups[offer.offer_type]) groups[offer.offer_type].push(offer);
    });

    return groups;
  }, [normalizedOffers]);

  const comboOffers = offersByType.combo;
  const addonOffers = offersByType.bill_unlock_addon;
  const billDiscountOffers = offersByType.bill_discount;
  const buyXGetYOffers = offersByType.buy_x_get_y;
  const timeSlotOffers = offersByType.time_slot;
  const progressiveOffers = offersByType.progressive_unlock;
  const coinRewardOffers = offersByType.coin_reward;
  const activeCoinRewardOffer = coinRewardOffers[0] || null;


  const normalCartSubtotal = useMemo(() => {
    return cart
      .filter((item) => !item.offerType)
      .reduce((sum, item) => sum + Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0), 0);
  }, [cart]);

  const isTimeSlotActiveClient = (offer) => {
    const now = new Date();
    const indiaParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const map = {};
    indiaParts.forEach((part) => {
      map[part.type] = part.value;
    });

    const dayMap = {
      Sun: 'sun',
      Mon: 'mon',
      Tue: 'tue',
      Wed: 'wed',
      Thu: 'thu',
      Fri: 'fri',
      Sat: 'sat',
    };

    const currentDay = dayMap[map.weekday] || '';
    const currentMinutes = Number(map.hour || 0) * 60 + Number(map.minute || 0);

    const parseMinutes = (value) => {
      const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
      if (!match) return null;
      return Number(match[1]) * 60 + Number(match[2]);
    };

    const start = parseMinutes(offer.start_time);
    const end = parseMinutes(offer.end_time);
    const days = safeArray(offer.days_active).map((day) =>
      String(day || '').slice(0, 3).toLowerCase()
    );

    if (start === null || end === null) return false;
    if (days.length && !days.includes(currentDay)) return false;

    return start <= end
      ? currentMinutes >= start && currentMinutes <= end
      : currentMinutes >= start || currentMinutes <= end;
  };

  const getProgressiveUnlockedStep = (offer) =>
    safeArray(offer.progressive_steps)
      .filter(
        (step) =>
          normalCartSubtotal >=
          Number(step.min_bill_amount || step.minBillAmount || step.amount || 0)
      )
      .sort(
        (a, b) =>
          Number(b.min_bill_amount || b.minBillAmount || b.amount || 0) -
          Number(a.min_bill_amount || a.minBillAmount || a.amount || 0)
      )[0] || null;

  const discountLikeOffers = useMemo(() => {
    const standard = billDiscountOffers.map((offer) => ({
      ...offer,
      eligible:
        normalCartSubtotal >= Number(offer.min_bill_amount || offer.min_purchasing_price || 0) &&
        (Number(offer.max_purchasing_price || 0) <= 0 ||
          normalCartSubtotal <= Number(offer.max_purchasing_price || 0)),
    }));

    const timed = timeSlotOffers.map((offer) => ({
      ...offer,
      eligible:
        isTimeSlotActiveClient(offer) &&
        normalCartSubtotal >= Number(offer.min_bill_amount || offer.min_purchasing_price || 0),
    }));

    const progressive = progressiveOffers.map((offer) => ({
      ...offer,
      unlocked_step: getProgressiveUnlockedStep(offer),
      eligible: Boolean(getProgressiveUnlockedStep(offer)),
    }));

    return [...standard, ...timed, ...progressive];
  }, [billDiscountOffers, timeSlotOffers, progressiveOffers, normalCartSubtotal]);

  const selectedSmartOffer = useMemo(
    () =>
      discountLikeOffers.find(
        (offer) => Number(offer.id) === Number(selectedSmartOfferId) && offer.eligible
      ) || null,
    [discountLikeOffers, selectedSmartOfferId]
  );

  useEffect(() => {
    if (
      selectedSmartOfferId &&
      !discountLikeOffers.some(
        (offer) => Number(offer.id) === Number(selectedSmartOfferId) && offer.eligible
      )
    ) {
      setSelectedSmartOfferId(null);
    }
  }, [discountLikeOffers, selectedSmartOfferId]);

  const bestBillDiscount = useMemo(() => {
    const eligible = billDiscountOffers
      .filter((offer) => normalCartSubtotal >= Number(offer.min_bill_amount || offer.min_purchasing_price || 0))
      .map((offer) => {
        const minBill = Number(offer.min_bill_amount || offer.min_purchasing_price || 0);
        const rawDiscount = offer.discount_type === 'percent'
          ? (normalCartSubtotal * Number(offer.discount_value || 0)) / 100
          : Number(offer.discount_value || 0);
        const maxDiscount = Number(offer.max_discount || 0);
        const discountAmount = maxDiscount > 0 ? Math.min(rawDiscount, maxDiscount) : rawDiscount;

        return {
          ...offer,
          minBill,
          discountAmount: Math.max(0, discountAmount),
        };
      })
      .filter((offer) => offer.discountAmount > 0)
      .sort((a, b) => b.discountAmount - a.discountAmount);

    return eligible[0] || null;
  }, [billDiscountOffers, normalCartSubtotal]);

  const nearestBillDiscount = useMemo(() => {
    return billDiscountOffers
      .filter((offer) => normalCartSubtotal < Number(offer.min_bill_amount || offer.min_purchasing_price || 0))
      .sort((a, b) => Number(a.min_bill_amount || a.min_purchasing_price || 0) - Number(b.min_bill_amount || b.min_purchasing_price || 0))[0] || null;
  }, [billDiscountOffers, normalCartSubtotal]);


  const coinEarnPreview = useMemo(() => {
    if (!activeCoinRewardOffer) {
      return {
        eligible: false,
        coins: 0,
        minBill: 0,
        earnPercent: 0,
        required: 0,
      };
    }

    const config = parseJsonMaybe(activeCoinRewardOffer.offer_json, {}) || {};
    const earnPercent = Number(
      config.coin_earn_percent ||
        activeCoinRewardOffer.coin_earn_percent ||
        activeCoinRewardOffer.reward_value ||
        0
    );
    const minBill = Number(
      config.coin_min_bill_amount ||
        activeCoinRewardOffer.min_bill_amount ||
        activeCoinRewardOffer.min_purchasing_price ||
        0
    );
    const minEarn = Math.max(
      0,
      Math.floor(
        Number(config.coin_min_earn || activeCoinRewardOffer.coin_min_earn || 0)
      )
    );
    const maxEarn = Math.max(
      0,
      Math.floor(
        Number(
          config.coin_max_earn_per_order ||
            activeCoinRewardOffer.coin_max_earn_per_order ||
            0
        )
      )
    );

    const eligibleAmount = Math.max(
      0,
      normalCartSubtotal - Number(pricingPreview.discountAmount || 0)
    );

    if (earnPercent <= 0 || eligibleAmount < minBill) {
      return {
        eligible: false,
        coins: 0,
        minBill,
        earnPercent,
        required: Math.max(0, minBill - eligibleAmount),
      };
    }

    let coins = Math.floor((eligibleAmount * earnPercent) / 100);
    coins = Math.max(minEarn, coins);
    if (maxEarn > 0) coins = Math.min(coins, maxEarn);

    return {
      eligible: coins > 0,
      coins: Math.max(0, coins),
      minBill,
      earnPercent,
      required: 0,
    };
  }, [activeCoinRewardOffer, normalCartSubtotal, pricingPreview.discountAmount]);

  const liveDiscountUnlockInfo = useMemo(() => {
    if (bestBillDiscount) {
      return {
        state: 'unlocked',
        title: 'Discount Unlocked',
        message: `${formatMoney(bestBillDiscount.discountAmount)} discount review me auto apply hoga.`,
        amount: 0,
        progress: 100,
        offer: bestBillDiscount,
      };
    }

    if (nearestBillDiscount) {
      const minBill = Number(nearestBillDiscount.min_bill_amount || nearestBillDiscount.min_purchasing_price || 0);
      const required = Math.max(0, minBill - normalCartSubtotal);
      const progress = minBill > 0 ? Math.min(100, Math.max(0, (normalCartSubtotal / minBill) * 100)) : 0;

      return {
        state: 'locked',
        title: 'Discount Unlock',
        message: `Add ${formatMoney(required)} more`,
        amount: required,
        progress,
        offer: nearestBillDiscount,
      };
    }

    return null;
  }, [bestBillDiscount, nearestBillDiscount, normalCartSubtotal]);



  const liveAddonUnlockInfo = useMemo(() => {
    if (!addonOffers.length) return null;

    const enriched = addonOffers.map((offer) => {
      const minBill = Number(offer.min_bill_amount || offer.min_purchasing_price || 0);
      const required = Math.max(0, minBill - normalCartSubtotal);
      const progress = minBill > 0 ? Math.min(100, Math.max(0, (normalCartSubtotal / minBill) * 100)) : 100;
      const product = normalizedProducts.find((item) => Number(item.id) === Number(offer.addon_product_id)) || null;

      return {
        offer,
        minBill,
        required,
        progress,
        productName: product?.name || offer.addon_product_name || offer.offer_name || 'Add-on Deal',
      };
    });

    const unlocked = enriched.find((item) => item.required <= 0);
    if (unlocked) {
      return {
        state: 'unlocked',
        title: 'Add-on Deal Unlocked',
        message: `${unlocked.productName} deal ab add kar sakte hain.`,
        amount: 0,
        progress: 100,
        offer: unlocked.offer,
      };
    }

    const nearest = enriched.sort((a, b) => a.required - b.required)[0];
    if (!nearest) return null;

    return {
      state: 'locked',
      title: 'Add-on Deal Unlock',
      message: `Add ${formatMoney(nearest.required)} more for ${nearest.productName}`,
      amount: nearest.required,
      progress: nearest.progress,
      offer: nearest.offer,
    };
  }, [addonOffers, normalCartSubtotal, normalizedProducts]);


  const offerSideHintInfo = useMemo(() => {
    if (!cart.length || normalCartSubtotal <= 0) return null;

    const candidates = [];

    if (liveDiscountUnlockInfo) {
      const offerId = liveDiscountUnlockInfo.offer?.id || 'discount';
      const amount = Number(liveDiscountUnlockInfo.amount || 0);
      const progress = Number(liveDiscountUnlockInfo.progress || 0);

      if (liveDiscountUnlockInfo.state === 'unlocked') {
        candidates.push({
          key: `discount-unlocked-${offerId}`,
          type: 'success',
          icon: '🎉',
          title: 'Discount unlocked',
          message: liveDiscountUnlockInfo.message,
        });
      } else if (amount > 0 && (amount <= 150 || progress >= 65)) {
        candidates.push({
          key: `discount-locked-${offerId}-${roundDisplayAmount(amount)}`,
          type: 'discount',
          icon: '🎁',
          title: 'Offer near hai',
          message: `${formatMoney(amount)} aur add karoge to discount mil sakta hai.`,
        });
      }
    }

    if (liveAddonUnlockInfo) {
      const offerId = liveAddonUnlockInfo.offer?.id || 'addon';
      const amount = Number(liveAddonUnlockInfo.amount || 0);
      const progress = Number(liveAddonUnlockInfo.progress || 0);

      if (liveAddonUnlockInfo.state === 'unlocked') {
        candidates.push({
          key: `addon-unlocked-${offerId}`,
          type: 'success',
          icon: '🔓',
          title: 'Deal unlocked',
          message: liveAddonUnlockInfo.message,
        });
      } else if (amount > 0 && (amount <= 150 || progress >= 65)) {
        candidates.push({
          key: `addon-locked-${offerId}-${roundDisplayAmount(amount)}`,
          type: 'addon',
          icon: '🍕',
          title: 'Deal close hai',
          message: liveAddonUnlockInfo.message,
        });
      }
    }

    const next = candidates[0] || null;
    if (!next) return null;
    if (next.key === offerHintDismissedKey) return null;
    return next;
  }, [cart.length, normalCartSubtotal, liveDiscountUnlockInfo, liveAddonUnlockInfo, offerHintDismissedKey]);

  const shopStatus = useMemo(() => getShopOpenStatus(orderSettings), [orderSettings]);

  const dineInEnabled = safeFlag(orderSettings.enable_dine_in, 1) === 1;
  const pickupEnabled = safeFlag(orderSettings.enable_pickup, 1) === 1;
  const deliveryEnabled = safeFlag(orderSettings.enable_home_delivery, 0) === 1;
  const codAllowed = safeFlag(orderSettings.allow_cod, 1) === 1;
  const onlineAllowed = safeFlag(orderSettings.allow_online_payment, 1) === 1;

  useEffect(() => {
    fetchInitialData();
  }, [vendorId, businessType, qrToken, resolvedQr]);

  useEffect(() => {
    const savedCustomerId = Number(
      foodRadarCustomerId ||
        localStorage.getItem('foodRadarCustomerId') ||
        0
    );

    if (!vendorId || !savedCustomerId) {
      setQrCoinWallet({ availableCoins: 0, pendingCoins: 0 });
      setQrCoinWalletError('');
      return;
    }

    fetchQrCoinWallet(savedCustomerId);
  }, [vendorId, businessType, foodRadarCustomerId]);


  useEffect(() => {
    if (step !== STEP_PRODUCTS) {
      setShowFloatingSearchShortcut(false);
      setShowBottomCategorySlider(false);
      return undefined;
    }

    const handleScroll = () => {
      const node = searchShellRef.current;
      if (!node) {
        setShowFloatingSearchShortcut(false);
        return;
      }

      const rect = node.getBoundingClientRect();
      setShowFloatingSearchShortcut(rect.bottom < 8);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [step]);

  useEffect(() => {
    if (!vendorId || businessType !== 'fastfood') return;

    const socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      query: {
        token: localStorage.getItem('token') || 'anonymous',
      },
    });

    socketRef.current = socket;

    const applyProductUpdates = (payload) => {
      if (!payload) return;

      const updates = Array.isArray(payload.updates) ? payload.updates : [payload];

      const relevant = updates.filter((item) => {
        const sameVendor = Number(item.vendorId || payload.vendorId || 0) === Number(vendorId);
        return sameVendor;
      });

      if (!relevant.length) return;

      setAllProducts((prev) =>
        prev.map((product) => {
          const found = relevant.find((u) => Number(u.productId || u.id) === Number(product.id));
          if (!found) return product;

          return {
            ...product,
            stock_quantity:
              found.stock_quantity == null ? product.stock_quantity : Number(found.stock_quantity || 0),
            is_active: found.is_active == null ? product.is_active : Number(found.is_active),
            availability: found.availability || product.availability,
          };
        })
      );
    };

    socket.on('stockUpdated', applyProductUpdates);
    socket.on('productUpdated', applyProductUpdates);
    socket.on('fastFoodStockUpdated', applyProductUpdates);

    return () => {
      socket.off('stockUpdated', applyProductUpdates);
      socket.off('productUpdated', applyProductUpdates);
      socket.off('fastFoodStockUpdated', applyProductUpdates);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [vendorId, businessType]);

  useEffect(() => {
    const safeSettings = normalizeOrderSettings(orderSettings);

    const finalDineIn = safeFlag(safeSettings.enable_dine_in, 1) === 1;
    const finalPickup = safeFlag(safeSettings.enable_pickup, 1) === 1;
    const finalDelivery = safeFlag(safeSettings.enable_home_delivery, 0) === 1;

    // QR-specific default ko sabse pehle preference milegi.
    // Agar requested mode vendor ne disable kiya hai to normal fallback chalega.
    if (isTableQr && finalDineIn) {
      setOrderMode('dine_in');
    } else if (isParcelQr && finalPickup) {
      setOrderMode('takeaway');
    } else if (finalDelivery) {
      setOrderMode('delivery');
    } else if (finalPickup) {
      setOrderMode('takeaway');
    } else if (finalDineIn) {
      setOrderMode('dine_in');
    }

    const finalCod = safeFlag(safeSettings.allow_cod, 1) === 1;
    const finalOnline = safeFlag(safeSettings.allow_online_payment, 1) === 1;

    setPaymentMode((current) => {
      if (finalCod && finalOnline) {
        return current === 'online' || current === 'cash' ? current : 'cash';
      }

      if (!finalCod && finalOnline) return 'online';
      if (finalCod && !finalOnline) return 'cash';

      return 'cash';
    });
  }, [orderSettings, isTableQr, isParcelQr]);

  useEffect(() => {
    if (orderMode !== 'delivery') {
      setCustomerLatitude('');
      setCustomerLongitude('');
      setCustomerLocationAccuracy(null);
      setCustomerLocationMessage('');
      setDeliveryDistanceKm(null);
    }
  }, [orderMode]);

  useEffect(() => {
    if (cart.length > 0) {
      fetchPricingPreview();
    } else {
      pricingPreviewRequestRef.current += 1;
      setPricingPreview({
        products: [],
        baseTotal: 0,
        deliveryCharge: 0,
        packagingCharge: 0,
        extraCharges: 0,
        discountAmount: 0,
        finalTotal: 0,
        validatedOfferName: null,
        appliedOffer: null,
        deliveryEtaMinutes: null,
      });
      setPricingPreviewReady(false);
      setPricingPreviewError('');
    }
  }, [cart, orderMode, orderSettings, selectedSmartOfferId]);

  useEffect(() => {
    if (step !== STEP_CUSTOMER || deviceProfileLookupDoneRef.current) {
      return undefined;
    }

    deviceProfileLookupDoneRef.current = true;
    fetchSavedDeviceProfile();

    return undefined;
  }, [step, businessType]);


  useEffect(() => {
    if (step !== STEP_REVIEW) {
      setShowLandingNotificationPrompt(false);
      return undefined;
    }

    if (notificationPermission !== 'default') {
      setShowLandingNotificationPrompt(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowLandingNotificationPrompt(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [step, notificationPermission]);


  useEffect(() => {
    if (
      notificationPermission !== 'granted' ||
      notificationTokenSaving
    ) {
      return;
    }

    const customerId = Number(
      foodRadarCustomerId ||
        placedOrder?.food_radar_customer_id ||
        localStorage.getItem('foodRadarCustomerId') ||
        0
    );

    const deviceKey = getCustomerPushDeviceKey();
    const syncKey = `${customerId || 0}:${deviceKey}:granted`;

    if (customerFcmSyncKeyRef.current === syncKey) {
      return;
    }

    customerFcmSyncKeyRef.current = syncKey;

    console.log(
      '[CUSTOMER FCM] Auto token sync start:',
      {
        customerId,
        step,
        reason: 'page-load-or-customer-resolved',
      }
    );

    setNotificationTokenSaving(true);

    getAndSaveCustomerFcmToken({
      customerId,
    })
      .then((freshToken) => {
        customerFcmLastSyncAtRef.current = Date.now();

        console.log(
          '[CUSTOMER FCM] Auto token sync success:',
          {
            customerId,
            tokenChanged:
              Boolean(
                freshToken &&
                freshToken !==
                  localStorage.getItem(
                    'foodRadarFcmTokenLastKnown'
                  )
              ),
          }
        );

        if (freshToken) {
          localStorage.setItem(
            'foodRadarFcmTokenLastKnown',
            freshToken
          );
        }
      })
      .catch((error) => {
        customerFcmSyncKeyRef.current = '';

        console.error(
          '[CUSTOMER FCM] Auto token sync failed:',
          error
        );
      })
      .finally(() => {
        setNotificationTokenSaving(false);
      });
  }, [
    step,
    notificationPermission,
    foodRadarCustomerId,
    placedOrder?.food_radar_customer_id,
  ]);


  useEffect(() => {
    if (
      notificationPermission !== 'granted'
    ) {
      return undefined;
    }

    let disposed = false;
    let syncing = false;

    const syncFreshCustomerFcmToken = async (
      reason = 'refresh'
    ) => {
      if (disposed || syncing) {
        return;
      }

      const customerId = Number(
        foodRadarCustomerId ||
          placedOrder?.food_radar_customer_id ||
          localStorage.getItem(
            'foodRadarCustomerId'
          ) ||
          0
      );

      const now = Date.now();
      const minimumGapMs = 60 * 1000;

      if (
        customerFcmLastSyncAtRef.current &&
        now -
          customerFcmLastSyncAtRef.current <
          minimumGapMs
      ) {
        return;
      }

      syncing = true;

      try {
        console.log(
          '[CUSTOMER FCM] Refresh token sync start:',
          {
            customerId,
            reason,
          }
        );

        const previousToken =
          localStorage.getItem(
            'foodRadarFcmToken'
          ) || '';

        const freshToken =
          await getAndSaveCustomerFcmToken({
            customerId,
          });

        customerFcmLastSyncAtRef.current =
          Date.now();

        if (freshToken) {
          localStorage.setItem(
            'foodRadarFcmTokenLastKnown',
            freshToken
          );
        }

        console.log(
          '[CUSTOMER FCM] Refresh token sync success:',
          {
            customerId,
            reason,
            tokenChanged:
              Boolean(
                previousToken &&
                freshToken &&
                previousToken !==
                  freshToken
              ),
          }
        );
      } catch (error) {
        console.warn(
          '[CUSTOMER FCM] Refresh token sync failed:',
          {
            reason,
            error:
              error?.message || error,
          }
        );
      } finally {
        syncing = false;
      }
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        syncFreshCustomerFcmToken(
          'page-visible'
        );
      }
    };

    const handleWindowFocus = () => {
      syncFreshCustomerFcmToken(
        'window-focus'
      );
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    window.addEventListener(
      'focus',
      handleWindowFocus
    );

    const intervalId = window.setInterval(
      () => {
        if (
          document.visibilityState ===
          'visible'
        ) {
          syncFreshCustomerFcmToken(
            'periodic'
          );
        }
      },
      30 * 60 * 1000
    );

    const initialTimer =
      window.setTimeout(
        () => {
          syncFreshCustomerFcmToken(
            'initial-visible-check'
          );
        },
        1500
      );

    return () => {
      disposed = true;

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      window.removeEventListener(
        'focus',
        handleWindowFocus
      );

      window.clearInterval(
        intervalId
      );

      window.clearTimeout(
        initialTimer
      );
    };
  }, [
    notificationPermission,
    foodRadarCustomerId,
    placedOrder?.food_radar_customer_id,
  ]);

  useEffect(() => {
    return () => {
      clearPaymentDismissTimer();

      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, []);

  const clearPaymentDismissTimer = () => {
    if (paymentDismissTimerRef.current) {
      clearTimeout(paymentDismissTimerRef.current);
      paymentDismissTimerRef.current = null;
    }
  };

  const clearMessages = () => {
    setMessage('');
    setErrorMessage('');
    setPaymentStatusMessage('');
  };

  const resolveQrTokenDetails = async () => {
    if (!qrToken) return null;

    const response = await axios.get(
      `${apiUrl}/public/order/qr-token/${encodeURIComponent(qrToken)}`
    );

    const data = response.data?.qr || response.data?.data || response.data || {};

    const nextQr = {
      vendorId: data.vendorId || data.vendor_id || '',
      businessType: String(data.businessType || data.business_type || 'fastfood').toLowerCase().trim(),
      qrName: data.qrName || data.qr_name || '',
      qrType: data.qrType || data.qr_type || data.type || '',
      tableNumber: data.tableNumber || data.table_number || '',
      qrToken: data.qrToken || data.qr_token || qrToken,
      isActive:
        data.isActive !== false &&
        Number(data.is_active ?? data.isActive ?? 1) !== 0 &&
        data.qrInactive !== true,
      message: data.message || '',
    };

    if (!nextQr.vendorId || nextQr.businessType !== 'fastfood') {
      throw new Error('Invalid fast food QR.');
    }

    setResolvedQr(nextQr);
    return nextQr;
  };

  const fetchVendorPublicProfile = async () => {
    // Correct route: orders publicRouter profile API
    // Mounted URL should be: /api/public/order/:businessType/profile
    try {
      return await axios.get(`${apiUrl}/public/order/${businessType}/profile`, {
        params: { vendorId },
      });
    } catch (primaryErr) {
      console.warn('Primary public profile route failed. Trying legacy route:', primaryErr?.message);

      // Backward-compatible fallback, agar server me old route mounted hai.
      return axios.get(`${apiUrl}/public/${businessType}/profile`, {
        params: { vendorId },
      });
    }
  };

  const getQrStatusParams = () => ({
    vendorId: Number(vendorId),
    vendorType: businessType,
    businessType,
    table: tableNumber || qrName || '',
    tableNumber: tableNumber || qrName || '',
    table_number: tableNumber || qrName || '',
    qrName: qrName || '',
    qr_name: qrName || '',
    qrToken: qrToken || '',
    qr_token: qrToken || '',
  });

  const checkQrActiveStatus = async () => {
    if ((!vendorId && !qrToken) || businessType !== 'fastfood') {
      return {
        isActive: false,
        message: 'Invalid fast food QR.',
      };
    }

    try {
      const response = await axios.get(`${apiUrl}/public/order/qr-status`, {
        params: getQrStatusParams(),
      });

      const data = response.data || {};
      const isActive =
        data.isActive !== false &&
        Number(data.is_active ?? data.isActive ?? 1) !== 0 &&
        data.qrInactive !== true;

      return {
        isActive,
        message:
          data.message ||
          (isActive
            ? ''
            : 'This QR code is not active. Please contact the shop.'),
      };
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.data?.qrInactive) {
        return {
          isActive: false,
          message:
            err.response?.data?.message ||
            'This QR code is not active. Please contact the shop.',
        };
      }

      throw err;
    }
  };

  const renderInactiveQrPage = () => (
    <div className="grocery-qr-wrapper fastfood-qr-wrapper qr-inactive-wrapper">
      <div className="grocery-qr-container">
        <div className="grocery-landing-card qr-inactive-card">
          <div className="qr-inactive-icon">!</div>
          <span className="grocery-chip">QR Checkout Off</span>
          <h2>QR Code Not Active</h2>
          <p>
            {qrAccess.message ||
              'This QR code is not active. Please contact the shop.'}
          </p>
          {qrHasDisplayLocation ? <small>{tableNumber ? `Table: ${tableNumber}` : `QR: ${qrDisplayLabel}`}</small> : null}
        </div>
      </div>
    </div>
  );


  const fetchInitialData = async () => {
    if (qrToken && !resolvedQr && !notificationTrackRequested) {
      setLoading(true);
      clearMessages();
      setQrAccess({
        checked: false,
        isActive: true,
        message: '',
      });

      try {
        const tokenQr = await resolveQrTokenDetails();

        if (!tokenQr?.isActive) {
          setQrAccess({
            checked: true,
            isActive: false,
            message:
              tokenQr?.message ||
              'This QR code is not active. Please contact the shop.',
          });
          setAllProducts([]);
          setOffers([]);
        }
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Invalid QR code. Please scan a valid QR code from the shop.';

        setQrAccess({
          checked: true,
          isActive: false,
          message: msg,
        });
        setErrorMessage('');
        setAllProducts([]);
        setOffers([]);
      } finally {
        setLoading(false);
        setOffersLoading(false);
      }

      return;
    }

    if (!vendorId || businessType !== 'fastfood') {
      setErrorMessage('Invalid fast food QR.');
      setLoading(false);
      return;
    }

    setLoading(true);
    clearMessages();
    setQrAccess({
      checked: false,
      isActive: true,
      message: '',
    });

    try {
      // Notification se khuli tracking QR checkout nahi hai. Isliye inactive
      // table/parcel QR customer ko apna existing order track karne se nahi rokega.
      // Actual order placement ke samay handlePlaceOrder QR ko dobara verify karta hai.
      const qrStatus = notificationTrackRequested
        ? { isActive: true, message: '' }
        : await checkQrActiveStatus();

      if (!qrStatus.isActive) {
        setQrAccess({
          checked: true,
          isActive: false,
          message: qrStatus.message,
        });
        setErrorMessage('');
        setAllProducts([]);
        setOffers([]);
        return;
      }

      setQrAccess({
        checked: true,
        isActive: true,
        message: '',
      });

      setOffersLoading(true);

      const [profileRes, productsRes, settingsRes, offersRes] = await Promise.all([
        fetchVendorPublicProfile(),
        axios.get(`${apiUrl}/public/products`, {
          params: { vendorId, businessType },
        }),
        axios.get(`${apiUrl}/public/order-settings`, {
          params: {
            vendorId,
            vendorType: businessType,
            userdb: 'true',
          },
        }),
        axios.get(`${apiUrl}/public/offers`, {
          params: {
            vendorId,
            vendorType: businessType,
            businessType,
            userdb: 'true',
          },
        }).catch(() => ({ data: { offers: [] } })),
      ]);

      const fixedProfilePayload = normalizePublicProfilePayload(profileRes.data || {});

      console.log('PUBLIC PROFILE RESPONSE:', profileRes.data);
      console.log('FIXED PROFILE PAYLOAD:', fixedProfilePayload);
      console.log('SHOP COORDINATE CHECK:', {
        shopLatitude: fixedProfilePayload.shop_latitude,
        shopLongitude: fixedProfilePayload.shop_longitude,
        hasShopCoordinates: isUsableCoordinatePair(
          fixedProfilePayload.shop_latitude,
          fixedProfilePayload.shop_longitude
        ),
      });

      setVendorProfile(fixedProfilePayload);

      const rawProducts = Array.isArray(productsRes.data)
        ? productsRes.data
        : safeArray(productsRes.data?.products || productsRes.data?.data);

      setAllProducts(rawProducts);
      setOrderSettings(normalizeOrderSettings(settingsRes.data || {}));

      const rawOffers = Array.isArray(offersRes.data)
        ? offersRes.data
        : safeArray(offersRes.data?.offers || offersRes.data?.data);

      setOffers(
        rawOffers
          .map((offer) => {
            try {
              return normalizeOffer(offer);
            } catch {
              return null;
            }
          })
          .filter(Boolean)
      );
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load fast food data.';

      setErrorMessage(msg);
    } finally {
      setLoading(false);
      setOffersLoading(false);
    }
  };

  const applyCustomerLookupData = (customer, mobile) => {
    if (!customer) return false;

    const nextName =
      customer.customer_name ||
      customer.customerName ||
      customer.name ||
      customer.delivery_details?.customer_name ||
      '';

    const nextMobile =
      customer.customer_mobile ||
      customer.customerMobile ||
      customer.mobile ||
      customer.customer_number ||
      customer.delivery_details?.customer_mobile ||
      mobile;

    const nextAddress1 =
      customer.address_line1 ||
      customer.addressLine1 ||
      customer.address ||
      customer.delivery_details?.address_line1 ||
      '';

    const nextAddress2 =
      customer.address_line2 ||
      customer.addressLine2 ||
      customer.delivery_details?.address_line2 ||
      '';

    const nextLandmark = customer.landmark || customer.delivery_details?.landmark || '';
    const nextCity = customer.city || customer.delivery_details?.city || '';
    const nextState = customer.state || customer.stateName || customer.delivery_details?.state || '';
    const nextPincode = customer.pincode || customer.delivery_details?.pincode || '';
    const nextNotes = customer.delivery_notes || customer.deliveryNotes || customer.notes || customer.delivery_details?.delivery_notes || '';

    const nextLatitude =
      customer.latitude ||
      customer.customer_latitude ||
      customer.delivery_details?.latitude ||
      customer.delivery_details?.customer_latitude ||
      '';

    const nextLongitude =
      customer.longitude ||
      customer.customer_longitude ||
      customer.delivery_details?.longitude ||
      customer.delivery_details?.customer_longitude ||
      '';

    const hasAnyDetails = Boolean(
      nextName ||
        nextAddress1 ||
        nextAddress2 ||
        nextLandmark ||
        nextCity ||
        nextState ||
        nextPincode ||
        nextNotes ||
        nextLatitude ||
        nextLongitude
    );

    if (!hasAnyDetails) return false;

    setCustomerName(nextName || '');
    setMobileNumber(normalizeMobile(nextMobile || mobile));
    setAddressLine1(nextAddress1 || '');
    setAddressLine2(nextAddress2 || '');
    setLandmark(nextLandmark || '');
    setCity(nextCity || '');
    setStateName(nextState || '');
    setPincode(String(nextPincode || '').replace(/\D/g, '').slice(0, 6));
    setDeliveryNotes(nextNotes || '');

    if (isValidLatitude(nextLatitude) && isValidLongitude(nextLongitude)) {
      setCustomerLatitude(String(nextLatitude));
      setCustomerLongitude(String(nextLongitude));

      if (hasShopCoordinates) {
        const distance = calculateDistanceKm(
          shopLatitude,
          shopLongitude,
          nextLatitude,
          nextLongitude
        );
        setDeliveryDistanceKm(distance);
      }
    }

    return true;
  };

  const fetchCustomerLookup = async (mobile) => {
    if (!vendorId || !mobile || mobile.length !== 10) return;

    setCustomerLookupLoading(true);
    setCustomerLookupMessage('');

    try {
      let customer = null;
      let exists = false;

      try {
        const response = await axios.get(`${apiUrl}/public/order/customer-lookup`, {
          params: {
            vendorId,
            vendorType: businessType,
            businessType,
            mobile,
            customer_number: mobile,
          },
        });

        customer = response.data?.customer || null;
        exists = Boolean(response.data?.exists && customer);

        // IMPORTANT:
        // Generic `customer` object ka `id` kabhi order/customer-history id ho sakta hai.
        // Food Radar ID sirf explicit Food Radar customer object se hi trust karenge.
        const explicitFoodRadarCustomer =
          response.data?.foodRadarCustomer ||
          response.data?.food_radar_customer ||
          response.data?.foodRadarProfile ||
          response.data?.food_radar_profile ||
          null;

        const lookedUpFoodRadarId = explicitFoodRadarCustomer
          ? getFoodRadarCustomerId(explicitFoodRadarCustomer)
          : null;

        if (lookedUpFoodRadarId) {
          setFoodRadarCustomerId(lookedUpFoodRadarId);
          localStorage.setItem('foodRadarCustomerId', String(lookedUpFoodRadarId));
          localStorage.setItem('foodRadarCustomerMobile', mobile);
        }
      } catch (lookupErr) {
        // Agar backend me customer-lookup endpoint abhi add nahi hua hai,
        // to existing /track endpoint se latest order ke customer details le lenge.
        const fallbackResponse = await axios.get(`${apiUrl}/public/order/track`, {
          params: {
            vendorId,
            vendorType: businessType,
            businessType,
            mobile,
            customer_number: mobile,
          },
        });

        const orders = Array.isArray(fallbackResponse.data?.orders)
          ? fallbackResponse.data.orders
          : [];

        customer = orders[0] || null;
        exists = Boolean(customer);
      }

      if (exists && applyCustomerLookupData(customer, mobile)) {
        setCustomerFound(true);
        setCustomerLookupMessage('Same mobile number mila. Details auto-fill ho gayi.');
      } else {
        setCustomerFound(false);
        setCustomerLookupMessage('New customer. Details fill kar dijiye, next time auto-fill ho jayega.');
      }
    } catch (err) {
      setCustomerFound(false);
      setCustomerLookupMessage('Customer details nahi mile. New customer ki tarah details fill kar sakte hain.');
    } finally {
      setCustomerLookupLoading(false);
    }
  };

  const applySavedDeviceProfile = (profile) => {
    if (!profile) return false;

    const applied = applyCustomerLookupData(
      profile,
      normalizeMobile(profile.customer_mobile || '')
    );

    if (!applied) return false;

    const accuracy = Number(profile.location_accuracy_meters || 0);
    setCustomerLocationAccuracy(accuracy > 0 ? accuracy : null);
    setSavedDeviceProfileApplied(true);
    setIsEditingCustomerDetails(false);
    setCustomerFound(true);
    setCustomerLookupMessage('');
    return true;
  };

  const fetchSavedDeviceProfile = async () => {
    const deviceKey = getCustomerPushDeviceKey();
    if (!deviceKey) return;

    try {
      setDeviceProfileLoading(true);
      setDeviceProfileMessage('');

      const response = await axios.get(
        `${apiUrl}/public/order/device-profile`,
        {
          params: {
            deviceKey,
            device_key: deviceKey,
            vendorType: businessType,
          },
        }
      );

      const profile = response.data?.profile || null;

      if (response.data?.exists && profile) {
        setSavedDeviceProfile(profile);
        applySavedDeviceProfile(profile);
      } else {
        setSavedDeviceProfile(null);
        setSavedDeviceProfileApplied(false);
      }
    } catch (error) {
      // Profile lookup optional hai; iske fail hone se ordering flow nahi rukega.
      console.warn('Saved device profile lookup failed:', error?.message || error);
      setSavedDeviceProfile(null);
      setSavedDeviceProfileApplied(false);
    } finally {
      setDeviceProfileLoading(false);
    }
  };

  const handleEnterNewAddress = () => {
    setAddressLine1('');
    setAddressLine2('');
    setLandmark('');
    setCity('');
    setStateName('');
    setPincode('');
    setDeliveryNotes('');
    setCustomerLatitude('');
    setCustomerLongitude('');
    setCustomerLocationAccuracy(null);
    setDeliveryDistanceKm(null);
    setCustomerLocationMessage('');
    setSavedDeviceProfileApplied(false);
    setIsEditingCustomerDetails(true);
    setDeviceProfileMessage('');
  };

  const handleForgetSavedAddress = async () => {
    const deviceKey = getCustomerPushDeviceKey();

    try {
      setDeviceProfileLoading(true);
      await axios.delete(`${apiUrl}/public/order/device-profile`, {
        data: {
          deviceKey,
          device_key: deviceKey,
          vendorType: businessType,
        },
      });

      setSavedDeviceProfile(null);
      setSavedDeviceProfileApplied(false);
      setIsEditingCustomerDetails(true);
      setDeviceProfileMessage('');
    } catch (error) {
      setDeviceProfileMessage(
        error.response?.data?.message || 'Saved address remove nahi hua. Dobara try karein.'
      );
    } finally {
      setDeviceProfileLoading(false);
    }
  };

  const handleUseCustomerLocation = (replaceAddress = false) => {
    if (!navigator.geolocation) {
      setCustomerLocationMessage('Aapke browser/device me location support nahi hai.');
      return;
    }

    setCustomerLocationLoading(true);
    setCustomerLocationMessage('Customer location detect ho rahi hai...');
    setErrorMessage('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = Number(position.coords.latitude).toFixed(8);
        const lng = Number(position.coords.longitude).toFixed(8);
        const accuracy = Number(position.coords.accuracy || 0);

        setCustomerLatitude(lat);
        setCustomerLongitude(lng);
        setCustomerLocationAccuracy(accuracy || null);

        const distance = hasShopCoordinates
          ? calculateDistanceKm(shopLatitude, shopLongitude, lat, lng)
          : null;

        setDeliveryDistanceKm(distance);

        if (distance !== null && deliveryRadiusKm > 0 && distance > deliveryRadiusKm) {
          setCustomerLocationMessage(
            `Location detect ho gayi, lekin delivery range ke bahar hai. Distance ${distance.toFixed(
              2
            )} KM hai. Shop sirf ${deliveryRadiusKm} KM tak delivery karta hai.`
          );
        } else if (distance !== null && deliveryRadiusKm > 0) {
          setCustomerLocationMessage(
            `Location detect ho gayi. Distance ${distance.toFixed(
              2
            )} KM hai. Delivery available hai.`
          );
        } else if (!hasShopCoordinates) {
          setCustomerLocationMessage(
            'Customer location detect ho gayi, lekin shop location missing hai. Vendor location check karein.'
          );
        } else {
          setCustomerLocationMessage(
            `Location detect ho gayi. Accuracy approx ${Math.round(
              accuracy || 0
            )} meter.`
          );
        }

        const address = await reverseGeocodeCustomerLocation(lat, lng);

        if (address) {
          if ((replaceAddress || !addressLine1) && address.addressLine1) {
            setAddressLine1(address.addressLine1);
          } else if ((replaceAddress || !addressLine1) && address.displayName) {
            setAddressLine1(address.displayName);
          }

          if ((replaceAddress || !addressLine2) && address.addressLine2) {
            setAddressLine2(address.addressLine2);
          }

          if ((replaceAddress || !city) && address.city) {
            setCity(address.city);
          }

          if ((replaceAddress || !stateName) && address.state) {
            setStateName(address.state);
          }

          if ((replaceAddress || !pincode) && address.pincode) {
            setPincode(address.pincode);
          }

        }

        setCustomerLocationLoading(false);
      },
      (error) => {
        console.error('Customer location error:', error);

        let msg = 'Location allow nahi hua. Please browser permission allow karein.';

        if (error.code === 1) {
          msg = 'Location permission denied hai. Browser me location allow karein.';
        } else if (error.code === 2) {
          msg = 'Location available nahi hai. GPS/Internet check karein.';
        } else if (error.code === 3) {
          msg = 'Location detect karne me timeout ho gaya. Dobara try karein.';
        }

        setCustomerLocationMessage(msg);
        setCustomerLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const categories = useMemo(() => {
    const fromProducts = normalizedProducts
      .filter((product) => {
        const activeOk = Number(product.is_active ?? 1) === 1;
        const availability = String(product.availability || '').toLowerCase();
        const stockOk =
          Number(product.stock_quantity ?? 999999) > 0 &&
          !['out_of_stock', 'unavailable', 'off'].includes(availability);

        return activeOk && stockOk;
      })
      .map((product) => String(product.category || '').trim())
      .filter(Boolean);

    return [...new Set(fromProducts)];
  }, [normalizedProducts]);

  const filteredProducts = useMemo(() => {
    return normalizedProducts.filter((product) => {
      const activeOk = Number(product.is_active ?? 1) === 1;

      const availability = String(product.availability || 'available').toLowerCase();
      const stockOk =
        product.stock_quantity == null ||
        Number(product.stock_quantity || 0) > 0 ||
        !['out_of_stock', 'unavailable', 'off'].includes(availability);

      const text = `${product.name || ''} ${product.category || ''} ${product.description || ''} ${product.food_type || ''}`.toLowerCase();

      const searchOk = !search.trim() || text.includes(search.trim().toLowerCase());
      const categoryOk = !categoryFilter || product.category === categoryFilter;
      const foodOk = !foodTypeFilter || String(product.food_type || 'veg').toLowerCase() === foodTypeFilter;
      const trendingOk = !showTrendingOnly || Number(product.trend_item) === 1;

      return activeOk && stockOk && searchOk && categoryOk && foodOk && trendingOk;
    });
  }, [normalizedProducts, search, categoryFilter, foodTypeFilter, showTrendingOnly]);

  const groupedProducts = useMemo(() => {
    const map = {};

    filteredProducts.forEach((product) => {
      const key = (product.category || 'Others').trim() || 'Others';
      if (!map[key]) map[key] = [];
      map[key].push(product);
    });

    return map;
  }, [filteredProducts]);

  const activeCartItems = useMemo(
    () => cart.filter((item) => Number(item.quantity || 0) > 0),
    [cart]
  );

  const cartCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }, [cart]);

  const cartEstimateTotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      return sum + Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0);
    }, 0);
  }, [cart]);

  const currentMinOrder = useMemo(() => {
    if (orderMode !== 'delivery') {
      return 0;
    }

    const globalMin = Number(orderSettings.min_order_amount || 0);
    const deliveryMin = Number(orderSettings.delivery_min_amount || 0);

    return Math.max(0, globalMin, deliveryMin);
  }, [orderSettings, orderMode]);

  const liveMinimumOrderInfo = useMemo(() => {
    if (!currentMinOrder || currentMinOrder <= 0) return null;

    const currentTotal = Number(cartEstimateTotal || 0);
    const required = Math.max(0, currentMinOrder - currentTotal);
    const progress = currentMinOrder > 0 ? Math.min(100, Math.max(0, (currentTotal / currentMinOrder) * 100)) : 0;

    return {
      reached: required <= 0,
      minOrder: currentMinOrder,
      required,
      progress,
      currentTotal,
    };
  }, [currentMinOrder, cartEstimateTotal]);

  const renderPublicPrice = (product) => {
    if (product.pricing_type === 'inquiry') return 'Price on Request';
    if (product.pricing_type === 'hidden' || !Number(product.show_price)) return 'Contact for Price';
    if (product.selling_price == null || product.selling_price === '') return 'Price Not Set';

    return formatMoney(product.selling_price);
  };

  const isUnavailable = (product) => {
    const availability = String(product.availability || 'available').toLowerCase();

    return (
      Number(product.is_active ?? 1) !== 1 ||
      availability === 'out_of_stock' ||
      availability === 'unavailable' ||
      availability === 'off'
    );
  };

  const getNormalCartKey = (productId) => `product_${Number(productId)}`;
  const getComboCartKey = (offerId) => `combo_${Number(offerId)}`;
  const getAddonCartKey = (offerId) => `addon_${Number(offerId)}`;

  const getCartItem = (productId) => {
    return cart.find((item) => item.cartKey === getNormalCartKey(productId)) || null;
  };

  const getCartItemByKey = (cartKey) => {
    return cart.find((item) => item.cartKey === cartKey) || null;
  };

  const findProductById = (productId) => {
    return normalizedProducts.find((item) => Number(item.id) === Number(productId)) || null;
  };

  const upsertCartItem = (product, nextQty) => {
    setCart((prev) => {
      const cartKey = getNormalCartKey(product.id);
      const existing = prev.find((item) => item.cartKey === cartKey);
      const finalQty = Math.max(0, Number(nextQty || 0));

      if (finalQty === 0) {
        return prev.filter((item) => item.cartKey !== cartKey);
      }

      const payload = {
        cartKey,
        itemType: 'product',
        productId: Number(product.id),
        id: Number(product.id),
        name: product.name,
        image: product.image_url || product.image || '',
        category: product.category || '',
        food_type: product.food_type || 'veg',
        description: product.description || '',
        selling_price: Number(product.selling_price || product.price || 0),
        price: Number(product.price || product.selling_price || 0),
        stock_quantity: Number(product.stock_quantity || 999999),
        quantity: finalQty,
      };

      if (existing) {
        return prev.map((item) => (item.cartKey === cartKey ? payload : item));
      }

      return [...prev, payload];
    });
  };

  const addProductToCart = (product) => {
    if (isUnavailable(product)) {
      setMessage(`"${product.name}" abhi available nahi hai.`);
      return;
    }

    const existing = getCartItem(product.id);
    const currentQty = Number(existing?.quantity || 0);
    const proposedQty = currentQty + 1;
    const stockQty = Number(product.stock_quantity || 999999);

    if (stockQty > 0 && proposedQty > stockQty) {
      setMessage(`"${product.name}" ke liye available stock sirf ${stockQty} hai.`);
      return;
    }

    upsertCartItem(product, proposedQty);
  };

  const increaseQty = (product) => {
    if (product?.offerType) {
      updateOfferCartQty(product.cartKey, Number(product.quantity || 0) + 1);
      return;
    }

    const realProduct = product.name ? product : findProductById(product.id || product.productId);
    if (!realProduct) return;

    const existing = getCartItem(realProduct.id);
    if (!existing) return addProductToCart(realProduct);

    const nextQty = Number(existing.quantity || 0) + 1;
    const stockQty = Number(existing.stock_quantity || realProduct.stock_quantity || 999999);

    if (stockQty > 0 && nextQty > stockQty) {
      setMessage(`"${realProduct.name}" ke liye available stock sirf ${stockQty} hai.`);
      return;
    }

    upsertCartItem(realProduct, nextQty);
  };

  const decreaseQty = (product) => {
    if (product?.offerType) {
      updateOfferCartQty(product.cartKey, Number(product.quantity || 0) - 1);
      return;
    }

    const realProduct = product.name ? product : findProductById(product.id || product.productId);
    if (!realProduct) return;

    const existing = getCartItem(realProduct.id);
    if (!existing) return;

    const nextQty = Number(existing.quantity || 0) - 1;
    upsertCartItem(realProduct, nextQty);
  };

  const removeFromCart = (productIdOrKey) => {
    if (String(productIdOrKey || '').includes('_')) {
      setCart((prev) => prev.filter((item) => item.cartKey !== productIdOrKey));
      return;
    }

    const realProduct = findProductById(productIdOrKey);

    if (!realProduct) {
      setCart((prev) => prev.filter((item) => Number(item.productId) !== Number(productIdOrKey)));
      return;
    }

    upsertCartItem(realProduct, 0);
  };

  const updateOfferCartQty = (cartKey, nextQty) => {
    const finalQty = Math.max(0, Number(nextQty || 0));

    setCart((prev) => {
      if (finalQty === 0) return prev.filter((item) => item.cartKey !== cartKey);

      return prev.map((item) => (item.cartKey === cartKey ? { ...item, quantity: finalQty } : item));
    });
  };

  const getOfferComboItems = (offer) => {
    return safeArray(offer.combo_items).map((item) => {
      const productId = Number(item.productId || item.id || item.product_id || 0);
      const product = findProductById(productId);
      const quantity = Math.max(1, Number(item.quantity || item.qty || 1));
      const price = Number(item.price || product?.selling_price || product?.price || 0);

      return {
        productId,
        id: productId,
        name: item.name || product?.name || 'Item',
        quantity,
        price,
        image: item.image || product?.image_url || product?.image || '',
        food_type: item.food_type || product?.food_type || 'veg',
      };
    }).filter((item) => item.productId && item.quantity > 0);
  };

  const getComboActualPrice = (offer) => {
    const savedActual = Number(offer.actual_price || 0);
    if (savedActual > 0) return savedActual;

    return getOfferComboItems(offer).reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  };

  const addComboOfferToCart = (offer) => {
    const comboItems = getOfferComboItems(offer);

    if (!comboItems.length) {
      setMessage('Combo items available nahi hai. Shop se contact karein.');
      return;
    }

    const cartKey = getComboCartKey(offer.id);
    const existing = getCartItemByKey(cartKey);
    const nextQty = Number(existing?.quantity || 0) + 1;
    const actualPrice = getComboActualPrice(offer);
    const comboPrice = Number(offer.combo_price || 0);

    setCart((prev) => {
      const payload = {
        cartKey,
        itemType: 'offer',
        offerType: 'combo',
        offerId: Number(offer.id),
        productId: cartKey,
        id: cartKey,
        name: offer.offer_name || 'Combo Offer',
        image: comboItems[0]?.image || '',
        category: 'Combo',
        food_type: 'veg',
        description: `Includes: ${comboItems.map((item) => `${item.name} x${item.quantity}`).join(', ')}`,
        selling_price: comboPrice,
        price: comboPrice,
        actual_price: actualPrice,
        customer_save: Math.max(0, actualPrice - comboPrice),
        quantity: nextQty,
        combo_items: comboItems,
      };

      const exists = prev.some((item) => item.cartKey === cartKey);
      return exists ? prev.map((item) => (item.cartKey === cartKey ? payload : item)) : [...prev, payload];
    });

    setMessage(`${offer.offer_name || 'Combo'} cart me add ho gaya.`);
  };

  const isAddonUnlocked = (offer) => {
    return normalCartSubtotal >= Number(offer.min_bill_amount || offer.min_purchasing_price || 0);
  };

  const addAddonOfferToCart = (offer) => {
    if (!isAddonUnlocked(offer)) {
      const required = Number(offer.min_bill_amount || offer.min_purchasing_price || 0) - normalCartSubtotal;
      setMessage(`${formatMoney(required)} aur add karein, phir ye deal unlock hogi.`);
      return;
    }

    const product = findProductById(offer.addon_product_id);

    if (!product) {
      setMessage('Add-on product available nahi hai. Shop se contact karein.');
      return;
    }

    if (isUnavailable(product)) {
      setMessage(`${product.name} abhi available nahi hai.`);
      return;
    }

    const cartKey = getAddonCartKey(offer.id);
    const existing = getCartItemByKey(cartKey);
    const limit = Math.max(1, Number(offer.limit_per_order || 1));
    const nextQty = Math.min(limit, Number(existing?.quantity || 0) + 1);

    if (existing && Number(existing.quantity || 0) >= limit) {
      setMessage(`Ye deal per order sirf ${limit} baar add ho sakti hai.`);
      return;
    }

    setCart((prev) => {
      const payload = {
        cartKey,
        itemType: 'offer',
        offerType: 'bill_unlock_addon',
        offerId: Number(offer.id),
        productId: cartKey,
        id: cartKey,
        linkedProductId: Number(product.id),
        name: `${product.name} Deal`,
        image: product.image_url || product.image || '',
        category: product.category || 'Deal',
        food_type: product.food_type || 'veg',
        description: `Unlocked on bill ${formatMoney(offer.min_bill_amount || offer.min_purchasing_price)}+`,
        selling_price: Number(offer.addon_special_price || 0),
        price: Number(offer.addon_special_price || 0),
        original_price: Number(offer.addon_original_price || product.selling_price || product.price || 0),
        quantity: nextQty,
        limit_per_order: limit,
      };

      const exists = prev.some((item) => item.cartKey === cartKey);
      return exists ? prev.map((item) => (item.cartKey === cartKey ? payload : item)) : [...prev, payload];
    });

    setMessage(`${product.name} deal cart me add ho gaya.`);
  };

  const addBuyXGetYOfferToCart = (offer) => {
    const buyProductId = Number(offer.buy_product_id || 0);
    const requiredQty = Math.max(1, Number(offer.buy_quantity || 1));

    const boughtQty = activeCartItems
      .filter(
        (item) =>
          !item.offerType &&
          Number(item.productId || item.id) === buyProductId
      )
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    if (boughtQty < requiredQty) {
      setMessage(`${requiredQty - boughtQty} quantity aur add karein, phir Buy X Get Y unlock hoga.`);
      return;
    }

    const cartKey = `buyxgety_${Number(offer.id)}`;

    if (getCartItemByKey(cartKey)) {
      setMessage('Ye Buy X Get Y offer already selected hai.');
      return;
    }

    setCart((prev) => [
      ...prev,
      {
        cartKey,
        itemType: 'offer',
        offerType: 'buy_x_get_y',
        offerId: Number(offer.id),
        productId: cartKey,
        id: cartKey,
        name: offer.offer_name || offer.offer_text || 'Buy X Get Y',
        image: '',
        category: 'Offer',
        food_type: 'veg',
        description: `Buy ${requiredQty}, get ${Math.max(1, Number(offer.get_quantity || 1))}`,
        selling_price: 0,
        price: 0,
        quantity: 1,
      },
    ]);

    setMessage(`${offer.offer_name || 'Buy X Get Y'} selected.`);
  };

  const buildOrderProductPayload = () => {
    return activeCartItems.map((item) => {
      if (item.offerType === 'combo') {
        return {
          type: 'combo',
          offerType: 'combo',
          offerId: Number(item.offerId),
          productId: item.productId,
          id: item.productId,
          name: item.name,
          price: Number(item.selling_price ?? item.price ?? 0),
          selling_price: Number(item.selling_price ?? item.price ?? 0),
          quantity: Number(item.quantity || 0),
          image: item.image,
          category: item.category,
          food_type: item.food_type || 'veg',
          description: item.description || '',
          comboItems: item.combo_items || [],
          combo_items: item.combo_items || [],
          original_price: Number(item.actual_price || 0),
          customer_save: Number(item.customer_save || 0),
          line_total: Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0),
        };
      }

      if (item.offerType === 'buy_x_get_y') {
        return {
          type: 'buy_x_get_y',
          offerType: 'buy_x_get_y',
          offerId: Number(item.offerId),
          productId: item.productId,
          id: item.productId,
          name: item.name,
          quantity: 1,
          price: 0,
          selling_price: 0,
          description: item.description || '',
        };
      }

      if (item.offerType === 'bill_unlock_addon') {
        return {
          type: 'addon_offer',
          offerType: 'bill_unlock_addon',
          offerId: Number(item.offerId),
          productId: item.linkedProductId || item.productId,
          id: item.linkedProductId || item.productId,
          name: item.name,
          price: Number(item.selling_price ?? item.price ?? 0),
          selling_price: Number(item.selling_price ?? item.price ?? 0),
          original_price: Number(item.original_price || 0),
          quantity: Number(item.quantity || 0),
          image: item.image,
          category: item.category,
          food_type: item.food_type || 'veg',
          description: item.description || '',
          line_total: Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0),
        };
      }

      return {
        type: 'product',
        productId: Number(item.productId),
        id: Number(item.productId),
        name: item.name,
        price: Number(item.selling_price ?? item.price ?? 0),
        selling_price: Number(item.selling_price ?? item.price ?? 0),
        quantity: Number(item.quantity || 0),
        image: item.image,
        category: item.category,
        food_type: item.food_type || 'veg',
        description: item.description || '',
        line_total: Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0),
      };
    });
  };

  const fetchPricingPreview = async () => {
    if (!vendorId || !cart.length) {
      pricingPreviewRequestRef.current += 1;
      setPricingPreviewReady(false);
      return;
    }

    const previewRequestId = pricingPreviewRequestRef.current + 1;
    pricingPreviewRequestRef.current = previewRequestId;

    const baseTotal = cart.reduce((sum, item) => {
      return sum + Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0);
    }, 0);

    const deliveryCharge = orderMode === 'delivery' ? Number(orderSettings.delivery_fee || 0) : 0;
    const packagingFee = orderMode === 'dine_in' ? 0 : Number(orderSettings.packaging_fee || 0);
    const extraCharges = deliveryCharge + packagingFee;
    // Kisi doosre smart offer ke select hone par bill-discount ka local amount
    // mat dikhayein. Exact discount backend preview se hi approve hoga.
    const discountAmount = selectedSmartOffer
      ? Number(
          Number(selectedSmartOffer.id) === Number(bestBillDiscount?.id)
            ? bestBillDiscount?.discountAmount || 0
            : 0
        )
      : Number(bestBillDiscount?.discountAmount || 0);
    const finalTotal = Math.max(0, baseTotal + extraCharges - discountAmount);

    setPricingPreviewReady(false);
    setPricingPreviewError('');
    setPricingPreview({
      products: cart.map((item) => ({
        ...item,
        line_total: Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0),
      })),
      baseTotal,
      deliveryCharge,
      packagingCharge: packagingFee,
      extraCharges,
      discountAmount,
      finalTotal,
      validatedOfferName:
        selectedSmartOffer?.offer_name ||
        bestBillDiscount?.offer_name ||
        null,
      appliedOffer: selectedSmartOffer || bestBillDiscount || null,
      deliveryEtaMinutes: orderMode === 'delivery' ? Number(orderSettings.delivery_eta_max || 0) : null,
    });

    try {
      setPreviewLoading(true);

      const response = await axios.post(`${apiUrl}/public/order/preview`, {
        vendorId: Number(vendorId),
        vendorType: businessType,
        order_mode: orderMode,
        order_type: 'individual',
        table: tableNumber || qrName || null,
        tableNumber: tableNumber || qrName || null,
        table_number: tableNumber || qrName || null,
        qrName: qrName || null,
        qr_name: qrName || null,
        qrToken: qrToken || null,
        qr_token: qrToken || null,
        products: buildOrderProductPayload(),
        appliedOfferId:
          selectedSmartOffer?.id ||
          bestBillDiscount?.id ||
          null,
      });

      const preview = response.data?.preview || response.data || {};

      // Purane cart/mode request ka late response latest amount ko overwrite na kare.
      if (previewRequestId !== pricingPreviewRequestRef.current) return;

      const hasBackendFinalTotal =
        preview &&
        (preview.finalTotal !== undefined ||
          preview.totalPrice !== undefined ||
          preview.final_total !== undefined);

      if (!hasBackendFinalTotal) {
        throw new Error('Backend se valid price preview nahi mila.');
      }

      if (hasBackendFinalTotal) {
        setPricingPreview({
          products: safeArray(preview.products).length ? safeArray(preview.products) : cart,
          baseTotal: Number(preview.baseTotal ?? preview.base_total ?? baseTotal ?? 0),
          deliveryCharge: Number(preview.deliveryCharge ?? preview.delivery_charge ?? 0),
          packagingCharge: Number(preview.packagingCharge ?? preview.packaging_charge ?? 0),
          extraCharges: Number(preview.extraCharges ?? preview.extra_charges ?? extraCharges ?? 0),
          discountAmount: Number(preview.discountAmount ?? preview.discount_amount ?? 0),
          finalTotal: Number(preview.finalTotal ?? preview.totalPrice ?? preview.final_total ?? 0),
          validatedOfferName: preview.validatedOfferName || preview.offer_name || selectedSmartOffer?.offer_name || bestBillDiscount?.offer_name || null,
          appliedOffer: preview.appliedOffer || selectedSmartOffer || bestBillDiscount || null,
          deliveryEtaMinutes: preview.deliveryEtaMinutes || null,
        });
        setPricingPreviewReady(true);
        setPricingPreviewError('');
      }
    } catch (error) {
      if (previewRequestId !== pricingPreviewRequestRef.current) return;

      const previewErrorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Final price verify nahi ho saka. Please dobara try karein.';

      setPricingPreviewReady(false);
      setPricingPreviewError(previewErrorMessage);
    } finally {
      if (previewRequestId === pricingPreviewRequestRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  const fetchTodayOrders = async ({
    mobileOverride = '',
    silent = false,
    focusOrderId = 0,
  } = {}) => {
    const mobile = normalizeMobile(mobileOverride || trackMobile);

    if (!mobile || !/^\d{10}$/.test(mobile)) {
      if (!silent) {
        setErrorMessage('Track order ke liye valid 10-digit mobile number required hai.');
      }
      return;
    }

    if (!vendorId) {
      if (!silent) setErrorMessage('Invalid vendor.');
      return;
    }

    try {
      if (!silent) {
        setTrackLoading(true);
        setTodayOrders([]);
        clearMessages();
      }

      const response = await axios.get(`${apiUrl}/public/order/track`, {
        params: {
          vendorId,
          vendorType: businessType,
          businessType,
          mobile,
        },
      });

      const orders = safeArray(response.data?.orders);
      const requestedOrderId = Number(focusOrderId || notificationTrackOrderId || 0);
      const focusedOrders = requestedOrderId
        ? orders.filter((order) => Number(order.id || order.order_id) === requestedOrderId)
        : orders;

      setTodayOrders(focusedOrders.length ? focusedOrders : orders);
      setTrackingNowMs(Date.now());

      if (!orders.length && !silent) {
        setMessage('Is mobile number se koi recent order nahi mila.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Order tracking failed.';

      if (!silent) setErrorMessage(msg);
    } finally {
      if (!silent) setTrackLoading(false);
    }
  };

  useEffect(() => {
    if (!notificationTrackRequested || !vendorId) return undefined;

    const savedMobile = normalizeMobile(
      trackMobile ||
        mobileNumber ||
        localStorage.getItem('foodRadarCustomerMobile') ||
        ''
    );

    if (!/^\d{10}$/.test(savedMobile)) return undefined;

    setTrackMobile(savedMobile);
    setStep(STEP_TRACK);
    fetchTodayOrders({
      mobileOverride: savedMobile,
      silent: true,
      focusOrderId: notificationTrackOrderId,
    });

    return undefined;
  }, [notificationTrackRequested, notificationTrackOrderId, vendorId, businessType]);

  useEffect(() => {
    const mobile = normalizeMobile(trackMobile);

    if (step !== STEP_TRACK || !vendorId || !/^\d{10}$/.test(mobile)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchTodayOrders({
        mobileOverride: mobile,
        silent: true,
        focusOrderId: notificationTrackOrderId,
      });
    }, 12000);

    return () => window.clearInterval(intervalId);
  }, [step, trackMobile, vendorId, businessType, notificationTrackOrderId]);

  useEffect(() => {
    if (step !== STEP_TRACK) return undefined;

    setTrackingNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setTrackingNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [step]);

  useEffect(() => {
    if (notificationPermission !== 'granted') return undefined;

    let disposed = false;
    let unsubscribe = null;

    const startForegroundOrderNotifications = async () => {
      const supported = await isMessagingSupported().catch(() => false);
      if (!supported || disposed) return;

      const messaging = getMessaging(getCustomerFirebaseApp());

      unsubscribe = onMessage(messaging, async (payload) => {
        if (disposed) return;

        const data = payload?.data || {};
        const notificationType = data.notificationType || data.notification_type || '';

        if (notificationType !== 'order_status') return;

        const title = data.title || 'Order Update';
        const body = data.body || 'Aapke order ka status update hua hai.';
        const deepLink =
          data.deepLink ||
          data.deep_link ||
          data.click_action ||
          data.link ||
          window.location.href;
        const shopImageUrl =
          data.shopImageUrl ||
          data.shop_image_url ||
          data.shopImage ||
          data.shop_image ||
          data.shopLogoUrl ||
          data.shop_logo_url ||
          '';

        try {
          const registration = await navigator.serviceWorker.getRegistration(
            CUSTOMER_SERVICE_WORKER_SCOPE
          );

          if (registration) {
            await registration.showNotification(title, {
              body,
              icon: shopImageUrl || '/logo192.png',
              badge: '/logo192.png',
              image: shopImageUrl || undefined,
              tag: `vakra-customer-order-${data.orderId || data.order_id || 'status'}-${data.orderStatus || data.order_status || ''}`,
              renotify: true,
              vibrate: [220, 100, 220, 100, 320],
              data: {
                ...data,
                deepLink,
                click_action: deepLink,
                link: deepLink,
              },
              actions: [{ action: 'track-order', title: 'Track Order' }],
            });
          }
        } catch (notificationError) {
          console.warn(
            '[CUSTOMER FCM] Foreground notification display failed:',
            notificationError?.message || notificationError
          );
        }

        if (step === STEP_TRACK && /^\d{10}$/.test(normalizeMobile(trackMobile))) {
          fetchTodayOrders({
            mobileOverride: trackMobile,
            silent: true,
            focusOrderId: Number(data.orderId || data.order_id || 0),
          });
        }
      });
    };

    startForegroundOrderNotifications();

    return () => {
      disposed = true;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [notificationPermission, step, trackMobile, vendorId, businessType]);

  const validateModeStep = () => {
    if (!shopStatus.isOpen) {
      setErrorMessage(shopStatus.message);
      return false;
    }

    if (orderMode === 'dine_in' && !dineInEnabled) {
      setErrorMessage('Dine-in abhi available nahi hai.');
      return false;
    }

    if (orderMode === 'takeaway' && !pickupEnabled) {
      setErrorMessage('Takeaway abhi available nahi hai.');
      return false;
    }

    if (orderMode === 'delivery' && !deliveryEnabled) {
      setErrorMessage('Home delivery abhi available nahi hai.');
      return false;
    }

    return true;
  };

  const validateCustomerStep = () => {
    const mobile = normalizeMobile(mobileNumber);

    if (paymentMode === 'online' && mobile.length !== 10) {
      setErrorMessage('Online payment ke liye valid 10-digit mobile number required hai.');
      return false;
    }

    if (orderMode === 'takeaway') {
      if (mobile.length !== 10) {
        setErrorMessage('Takeaway ke liye valid 10-digit mobile number required hai.');
        return false;
      }

      if (!customerName.trim()) {
        setErrorMessage('Takeaway ke liye customer name required hai.');
        return false;
      }
    }

    if (orderMode === 'delivery') {
      if (mobile.length !== 10) {
        setErrorMessage('Delivery ke liye valid 10-digit mobile number required hai.');
        return false;
      }

      if (!customerName.trim()) {
        setErrorMessage('Delivery ke liye customer name required hai.');
        return false;
      }

      if (!addressLine1.trim()) {
        setErrorMessage('Delivery ke liye address line 1 required hai.');
        return false;
      }

      if (!city.trim()) {
        setErrorMessage('Delivery ke liye city required hai.');
        return false;
      }

      if (!stateName.trim()) {
        setErrorMessage('Delivery ke liye state required hai.');
        return false;
      }

      if (!/^\d{6}$/.test(pincode)) {
        setErrorMessage('Delivery ke liye valid 6-digit pincode required hai.');
        return false;
      }

      if (deliveryRadiusKm > 0) {
        if (!hasShopCoordinates) {
          setErrorMessage('Shop location set nahi hai. Delivery order place nahi ho sakta.');
          return false;
        }

        if (!hasCustomerCoordinates) {
          setErrorMessage('Delivery ke liye customer location allow karna required hai.');
          return false;
        }

        if (!deliveryRangeStatus.allowed) {
          setErrorMessage(deliveryRangeStatus.message);
          return false;
        }
      }
    }

    if (mobileNumber && mobile.length !== 10) {
      setErrorMessage('Mobile number 10 digit ka hona chahiye.');
      return false;
    }

    return true;
  };

  const validateCartBeforeReview = () => {
    if (!cart.length) {
      setErrorMessage('Please kam se kam ek item add karein.');
      return false;
    }

    const selectedAmount = Number(cartEstimateTotal || 0);
    const selectedMinOrder = Number(currentMinOrder || 0);

    if (selectedMinOrder > 0 && selectedAmount < selectedMinOrder) {
      const required = Math.max(0, selectedMinOrder - selectedAmount);
      const popupPayload = {
        key: Date.now(),
        minOrder: selectedMinOrder,
        currentTotal: selectedAmount,
        required,
      };

      setMinOrderPopup(popupPayload);
      setErrorMessage('');
      setMessage(`Minimum order ${formatMoney(selectedMinOrder)} hai. Review ke liye ${formatMoney(required)} aur add karein.`);
      return false;
    }

    return true;
  };

  const validatePaymentStep = () => {
    if (!codAllowed && !onlineAllowed) {
      setErrorMessage('Shop ne payment method enable nahi kiya hai.');
      return false;
    }

    if (paymentMode === 'cash' && !codAllowed) {
      setErrorMessage('Cash / COD allowed nahi hai.');
      return false;
    }

    if (paymentMode === 'online' && !onlineAllowed) {
      setErrorMessage('Online payment allowed nahi hai.');
      return false;
    }

    const mobile = normalizeMobile(mobileNumber);

    if (paymentMode === 'online' && mobile.length !== 10) {
      setErrorMessage('Online payment ke liye valid 10-digit mobile number required hai.');
      return false;
    }

    return true;
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');

      if (existingScript) {
        existingScript.onload = () => resolve(true);
        existingScript.onerror = () => resolve(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });

  const createPaymentOrder = async ({ products, orderMode, orderType, customerNumber }) => {
    const safeCustomer = normalizeMobile(customerNumber);

    /*
      Online payment ke liye mobile mandatory hai.
      Walking cash customer ke liye koi dummy mobile fallback use nahi hoga.
    */
    if (safeCustomer.length !== 10) {
      throw new Error('Online payment ke liye valid 10-digit mobile number required hai.');
    }

    const idempotencyKey = `pay_${vendorId}_${businessType}_${safeCustomer}_${Date.now()}`;

    const response = await axios.post(`${apiUrl}/public/payment/create-order`, {
      vendorId: parseInt(vendorId, 10),
      vendorType: businessType,
      products,
      orderMode,
      orderType,
      customerNumber: safeCustomer,
      receipt: idempotencyKey,
      idempotencyKey,
    });

    return response.data;
  };

  const verifyOnlinePayment = async ({
    paymentId,
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
  }) => {
    const response = await axios.post(`${apiUrl}/public/payment/verify`, {
      vendorId: parseInt(vendorId, 10),
      vendorType: businessType,
      paymentId,
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
    });

    return response.data;
  };

  const openRazorpayCheckout = async ({ paymentData, orderTotal }) => {
    const sdkLoaded = await loadRazorpayScript();

    if (!sdkLoaded) {
      throw new Error('Razorpay SDK load nahi hua. Please internet check karein.');
    }

    return new Promise((resolve, reject) => {
      let successHandled = false;

      const options = {
        key: paymentData.key,
        amount: paymentData.amount,
        currency: paymentData.currency || 'INR',
        name: shopName || 'Fast Food Shop',
        description: 'Fast Food Order Payment',
        order_id: paymentData.razorpay_order_id,
        prefill: {
          name: customerName || '',
          contact: normalizeMobile(mobileNumber),
        },
        notes: {
          vendorId: String(vendorId),
          vendorType: businessType,
          orderMode,
        },
        theme: {
          color: '#e67e22',
        },
        modal: {
          confirm_close: true,
          escape: false,
          backdropclose: false,
          ondismiss: () => {
            if (successHandled) return;

            setIsPaymentVerifying(true);
            setPaymentStatusMessage('Payment verify ho raha hai, please wait.');
            setMessage('Payment verify ho raha hai, please wait.');

            clearPaymentDismissTimer();

            paymentDismissTimerRef.current = setTimeout(() => {
              if (successHandled) return;

              setIsPaymentVerifying(false);
              setPaymentStatusMessage('');
              setMessage('Payment complete nahi hua ya confirmation late aaya. Aap dobara try kar sakte hain.');
              reject(new Error('Payment popup band hua, lekin success confirmation nahi mila.'));
            }, 15000);
          },
        },
        handler: async function (response) {
          try {
            successHandled = true;
            clearPaymentDismissTimer();

            setIsPaymentVerifying(true);
            setPaymentStatusMessage('Payment verify ho raha hai, please wait.');
            setMessage('Payment verify ho raha hai, please wait.');

            await verifyOnlinePayment({
              paymentId: paymentData.paymentId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            setIsPaymentVerifying(false);
            setPaymentStatusMessage('');
            setMessage('');

            resolve({
              success: true,
              paymentStatus: 'paid',
              paymentMethod: 'razorpay',
              paymentReferenceId: paymentData.paymentId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              total: orderTotal,
            });
          } catch (err) {
            clearPaymentDismissTimer();
            setIsPaymentVerifying(false);
            setPaymentStatusMessage('');
            reject(new Error(err?.response?.data?.error || err.message || 'Payment verify failed'));
          }
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', function (response) {
        clearPaymentDismissTimer();
        setIsPaymentVerifying(false);
        setPaymentStatusMessage('');

        reject(
          new Error(
            response?.error?.description ||
              response?.error?.reason ||
              'Payment failed'
          )
        );
      });

      rzp.open();
    });
  };

  const generateToken = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const generateRedeemCode = () => {
    const left = Math.random().toString(36).substring(2, 6).toUpperCase();
    const right = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${left}-${right}`;
  };

  const getExpiryDateString = () => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 7);
    return expiry.toISOString().slice(0, 19).replace('T', ' ');
  };

  const saveRedeemCode = async ({ orderId, token, redeemCode, expiryDate }) => {
    const response = await axios.post(`${apiUrl}/public/order/redeem/save`, {
      redeemCode,
      expiryDate,
      orderId,
      token,
      vendorId,
      vendorType: businessType,
    });

    return response.data;
  };

  const buildDeliveryDetailsPayload = () => {
    if (orderMode !== 'delivery') {
      return {
        customer_name: customerName || null,
        customer_mobile: mobileNumber || null,
      };
    }

    return {
      customer_name: customerName || null,
      customer_mobile: mobileNumber || null,
      address_line1: addressLine1 || null,
      address_line2: addressLine2 || null,
      landmark: landmark || null,
      city: city || null,
      state: stateName || null,
      pincode: pincode || null,
      delivery_notes: deliveryNotes || null,

      latitude: hasCustomerCoordinates ? Number(customerLatitude) : null,
      longitude: hasCustomerCoordinates ? Number(customerLongitude) : null,

      customer_latitude: hasCustomerCoordinates ? Number(customerLatitude) : null,
      customer_longitude: hasCustomerCoordinates ? Number(customerLongitude) : null,

      delivery_distance_km:
        deliveryRangeStatus.distanceKm !== null && deliveryRangeStatus.distanceKm !== undefined
          ? Number(Number(deliveryRangeStatus.distanceKm).toFixed(2))
          : deliveryDistanceKm !== null && deliveryDistanceKm !== undefined
            ? Number(Number(deliveryDistanceKm).toFixed(2))
            : null,

      location_accuracy_meters: customerLocationAccuracy
        ? Math.round(Number(customerLocationAccuracy))
        : null,
    };
  };

  const fetchQrCoinWallet = async (customerIdOverride = null) => {
    const finalCustomerId = Number(
      customerIdOverride ||
        foodRadarCustomerId ||
        localStorage.getItem('foodRadarCustomerId') ||
        0
    );

    if (!finalCustomerId || !vendorId) {
      setQrCoinWallet({ availableCoins: 0, pendingCoins: 0 });
      setQrCoinWalletError('');
      return;
    }

    try {
      setQrCoinWalletLoading(true);
      setQrCoinWalletError('');

      const savedMobile = normalizeMobile(
        localStorage.getItem('foodRadarCustomerMobile') ||
          mobileNumber ||
          ''
      );

      const response = await axios.get(
        `${apiUrl}/public/food-radar/rewards`,
        {
          params: {
            customerId: finalCustomerId,
            customer_id: finalCustomerId,
            mobile: savedMobile || undefined,
          },
        }
      );

      const source =
        response.data?.vendors ||
        response.data?.rewards ||
        response.data?.wallets ||
        response.data?.data ||
        [];

      const wallets = Array.isArray(source) ? source : [];
      const normalizedBusinessType = String(businessType || '')
        .toLowerCase()
        .trim();

      const currentVendorWallet = wallets.find((item) => {
        const itemVendorId = Number(
          item?.vendorId ||
            item?.vendor_id ||
            item?.id ||
            0
        );
        const itemVendorType = String(
          item?.vendorType ||
            item?.vendor_type ||
            item?.businessType ||
            item?.business_type ||
            ''
        )
          .toLowerCase()
          .trim();

        return (
          itemVendorId === Number(vendorId) &&
          (!itemVendorType || itemVendorType === normalizedBusinessType)
        );
      });

      const availableCoins = Math.max(
        0,
        Math.floor(
          safeNumber(
            currentVendorWallet?.available_coins ??
              currentVendorWallet?.availableCoins ??
              currentVendorWallet?.customer_available_coins ??
              currentVendorWallet?.coins_available ??
              currentVendorWallet?.balance ??
              0,
            0
          )
        )
      );

      const pendingCoins = Math.max(
        0,
        Math.floor(
          safeNumber(
            currentVendorWallet?.pending_coins ??
              currentVendorWallet?.pendingCoins ??
              currentVendorWallet?.customer_pending_coins ??
              currentVendorWallet?.coins_pending ??
              0,
            0
          )
        )
      );

      setQrCoinWallet({ availableCoins, pendingCoins });
    } catch (error) {
      console.warn(
        'QR coin wallet fetch failed:',
        error?.response?.data?.message || error?.message || error
      );
      setQrCoinWalletError('Coin balance abhi load nahi ho saka.');
    } finally {
      setQrCoinWalletLoading(false);
    }
  };

  const ensureFoodRadarCustomerProfile = async () => {
    const mobile = normalizeMobile(mobileNumber);

    const savedId = Number(
      foodRadarCustomerId ||
        localStorage.getItem('foodRadarCustomerId') ||
        0
    );
    const savedMobile = normalizeMobile(
      localStorage.getItem('foodRadarCustomerMobile') || ''
    );

    // Same browser/device pe customer pehle identify ho chuka hai to
    // dine-in/takeaway me mobile blank hone par bhi wahi customer ID use karo.
    if (savedId > 0 && mobile.length !== 10) {
      return savedId;
    }

    if (savedId > 0 && (!savedMobile || savedMobile === mobile)) {
      return savedId;
    }

    // New/unknown QR device me mobile optional hai.
    // Valid mobile nahi mila to anonymous device flow continue karega.
    if (mobile.length !== 10) return null;

    try {
      const response = await axios.post(
        `${apiUrl}/public/food-radar/customers/register`,
        {
          name: customerName || 'QR Customer',
          mobile,
          email: '',
          default_address: orderMode === 'delivery' ? addressLine1 || '' : '',
          area: 'QR Order',
          city: city || '',
        }
      );

      const customer =
        response.data?.customer ||
        response.data?.data ||
        response.data?.profile ||
        null;

      const resolvedId = getFoodRadarCustomerId(customer);

      if (resolvedId) {
        setFoodRadarCustomerId(resolvedId);
        localStorage.setItem('foodRadarCustomerId', String(resolvedId));
        localStorage.setItem('foodRadarCustomerMobile', mobile);
        return resolvedId;
      }
    } catch (error) {
      console.warn('QR coin customer profile link failed:', error?.message || error);
    }

    return null;
  };


  const saveCustomerFcmToken = async ({
    customerId,
    fcmToken,
  }) => {
    const finalCustomerId = Number(customerId || 0) || null;
    const finalToken = String(fcmToken || '').trim();
    const deviceKey = getCustomerPushDeviceKey();

    if (!finalToken || !deviceKey) return false;

    const response = await axios.post(
      `${apiUrl}/public/food-radar/customers/fcm-token`,
      {
        customerId: finalCustomerId,
        customer_id: finalCustomerId,
        foodRadarCustomerId: finalCustomerId,
        food_radar_customer_id: finalCustomerId,
        mobile: normalizeMobile(mobileNumber),
        fcmToken: finalToken,
        fcm_token: finalToken,
        token: finalToken,
        deviceKey,
        device_key: deviceKey,
        platform: 'web',
        source: 'qr-order',
        permissionStatus: 'granted',
        permission_status: 'granted',
        userAgent:
          typeof navigator !== 'undefined'
            ? navigator.userAgent || ''
            : '',
      }
    );

    if (!response.data || response.data?.success !== true) {
      throw new Error(
        response.data?.error ||
          response.data?.message ||
          'Customer notification token database me save confirm nahi hua.'
      );
    }

    localStorage.setItem('foodRadarFcmToken', finalToken);
    if (finalCustomerId) {
      localStorage.setItem(
        'foodRadarFcmCustomerId',
        String(finalCustomerId)
      );
    }
    localStorage.setItem(
      'foodRadarNotificationsEnabled',
      '1'
    );

    return finalToken;
  };

  const recordCustomerVendorTouch = async ({
    eventType = 'qr_scan',
    customerId = null,
  } = {}) => {
    if (!vendorId || businessType !== 'fastfood') {
      return false;
    }

    const deviceKey = getCustomerPushDeviceKey();

    if (!deviceKey) return false;

    try {
      const response = await axios.post(
        `${apiUrl}/control-center/notifications/public/qr-visit`,
        {
          vendorId: Number(vendorId),
          vendor_id: Number(vendorId),
          customerId: Number(customerId || 0) || null,
          customer_id: Number(customerId || 0) || null,
          deviceKey,
          device_key: deviceKey,
          eventType,
          event_type: eventType,
          qrName: qrName || '',
          qr_name: qrName || '',
          tableNumber: tableNumber || '',
          table_number: tableNumber || '',
          businessType,
          business_type: businessType,
          source: 'qr-order',
        }
      );

      return Boolean(response.data?.success);
    } catch (error) {
      console.warn(
        '[FOOD RADAR VISIT] Tracking skipped:',
        error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.message ||
          error
      );
      return false;
    }
  };


  useEffect(() => {
    if (!vendorId || businessType !== 'fastfood') {
      return;
    }

    const customerId = Number(
      foodRadarCustomerId ||
        placedOrder?.food_radar_customer_id ||
        localStorage.getItem('foodRadarCustomerId') ||
        0
    ) || null;

    recordCustomerVendorTouch({
      eventType: 'qr_scan',
      customerId,
    });
  }, [
    vendorId,
    businessType,
    qrName,
    tableNumber,
    foodRadarCustomerId,
    placedOrder?.food_radar_customer_id,
  ]);


  const getAndSaveCustomerFcmToken = async ({
    customerId,
  }) => {
    if (
      typeof window === 'undefined' ||
      typeof navigator === 'undefined'
    ) {
      throw new Error('Browser notification available nahi hai.');
    }

    if (!('serviceWorker' in navigator)) {
      throw new Error(
        'Is browser me service worker supported nahi hai.'
      );
    }

    if (!hasCustomerFirebaseConfig()) {
      console.error(
        '[CUSTOMER FCM] Firebase frontend config missing:',
        {
          apiKey: Boolean(customerFirebaseConfig.apiKey),
          projectId: Boolean(customerFirebaseConfig.projectId),
          messagingSenderId: Boolean(
            customerFirebaseConfig.messagingSenderId
          ),
          appId: Boolean(customerFirebaseConfig.appId),
          vapidKey: Boolean(customerFirebaseVapidKey),
        }
      );

      throw new Error(
        'Customer Firebase frontend config missing hai.'
      );
    }

    const supported =
      await isMessagingSupported().catch(() => false);

    if (!supported) {
      throw new Error(
        'Is browser me Firebase notifications supported nahi hain.'
      );
    }

    const registration =
      await navigator.serviceWorker.register(
        CUSTOMER_SERVICE_WORKER_PATH,
        {
          scope: CUSTOMER_SERVICE_WORKER_SCOPE,
          updateViaCache: 'none',
        }
      );

    try {
      await registration.update();
    } catch {}

    const firebaseApp = getCustomerFirebaseApp();
    const messaging = getMessaging(firebaseApp);

    /*
      AUTO RECOVERY:
      Browser permission "granted" reh sakti hai lekin Firebase purane
      registration token ko NotRegistered kar sakta hai.

      Har NEW BROWSER SESSION me ek baar current Firebase registration
      refresh karenge. Same browser session me baar-baar vendor QR scan
      karne par token rotate nahi hoga.
    */
    const sessionRefreshKey =
      `foodRadarFcmFreshSession:${getCustomerPushDeviceKey()}`;

    const alreadyRefreshedThisSession =
      sessionStorage.getItem(sessionRefreshKey) === '1';

    if (!alreadyRefreshedThisSession) {
      try {
        console.log(
          '[CUSTOMER FCM] Fresh session token regeneration start:',
          { customerId }
        );

        await deleteToken(messaging);

        console.log(
          '[CUSTOMER FCM] Old browser FCM registration cleared.'
        );
      } catch (deleteError) {
        console.warn(
          '[CUSTOMER FCM] Old token delete skipped:',
          deleteError?.message || deleteError
        );
      }
    }

    const fcmToken = await getToken(messaging, {
      vapidKey: customerFirebaseVapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!fcmToken) {
      throw new Error('Customer FCM token generate nahi hua.');
    }

    const savedToken = await saveCustomerFcmToken({
      customerId,
      fcmToken,
    });

    if (!savedToken) {
      throw new Error(
        'Customer FCM token database me save nahi hua.'
      );
    }

    sessionStorage.setItem(
      sessionRefreshKey,
      '1'
    );

    localStorage.setItem(
      'foodRadarFcmTokenLastKnown',
      fcmToken
    );

    localStorage.setItem(
      'foodRadarFcmLastSyncedAt',
      String(Date.now())
    );

    setNotificationTokenSaved(true);

    console.log(
      '[CUSTOMER FCM] Current device token active:',
      {
        customerId,
        tokenStart: `${fcmToken.slice(0, 18)}...`,
        freshSession:
          !alreadyRefreshedThisSession,
      }
    );

    return fcmToken;
  };

  const handleEnableCustomerNotifications = async () => {
    if (typeof Notification === 'undefined') {
      setNotificationPermission('unsupported');
      setNotificationMessage(
        'Is browser me notifications supported nahi hain.'
      );
      return;
    }

    if (notificationTokenSaving) return;

    try {
      setNotificationTokenSaving(true);
      setNotificationMessage('');

      let permission = Notification.permission;
      const previousPermission = permission;

      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      setNotificationPermission(permission);

      if (permission !== 'granted') {
        setNotificationMessage(
          permission === 'denied'
            ? 'Notifications block hain. Browser Site Settings se Allow kar sakte hain.'
            : 'Notification permission allow nahi hui.'
        );
        return;
      }

      let customerId =
        Number(
          placedOrder?.food_radar_customer_id ||
            foodRadarCustomerId ||
            localStorage.getItem('foodRadarCustomerId') ||
            0
        ) || null;

      if (!customerId) {
        customerId = await ensureFoodRadarCustomerProfile();
      }

      await getAndSaveCustomerFcmToken({
        customerId: customerId || null,
      });

      if (
        previousPermission !== 'granted' &&
        permission === 'granted'
      ) {
        await recordCustomerVendorTouch({
          eventType: 'notification_allowed',
          customerId: customerId || null,
        });
      }

      setNotificationMessage(
        'Notifications enabled. Order updates aur Food Radar offers mil sakte hain.'
      );
    } catch (error) {
      console.warn(
        'Customer notification enable failed:',
        error?.message || error
      );

      setNotificationMessage(
        error?.message ||
          'Notification enable nahi hui. Please dobara try karein.'
      );
    } finally {
      setNotificationTokenSaving(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (previewLoading || !pricingPreviewReady) {
      setErrorMessage(
        pricingPreviewError ||
          'Final price verify nahi hua hai. Please price verify hone ke baad order place karein.'
      );
      return;
    }

    if (qrAccess.checked && !qrAccess.isActive) {
      setErrorMessage(
        qrAccess.message ||
          'This QR code is not active. Please contact the shop.'
      );
      return;
    }

    try {
      const latestQrStatus = await checkQrActiveStatus();

      if (!latestQrStatus.isActive) {
        setQrAccess({
          checked: true,
          isActive: false,
          message:
            latestQrStatus.message ||
            'This QR code is not active. Please contact the shop.',
        });
        setErrorMessage(
          latestQrStatus.message ||
            'This QR code is not active. Please contact the shop.'
        );
        return;
      }
    } catch (qrCheckErr) {
      const qrMsg =
        qrCheckErr.response?.data?.message ||
        qrCheckErr.response?.data?.error ||
        qrCheckErr.message ||
        'QR status check failed. Please try again.';
      setErrorMessage(qrMsg);
      return;
    }

    if (!validateCartBeforeReview()) return;
    if (!validateCustomerStep()) return;
    if (!validatePaymentStep()) return;

    try {
      setPlacingOrder(true);
      clearMessages();

      const safeCustomerNumber = normalizeMobile(mobileNumber) || '';

      const orderProducts = buildOrderProductPayload();

      let paymentSnapshot = null;

      if (paymentMode === 'online') {
        setPaymentLoading(true);

        const paymentData = await createPaymentOrder({
          products: orderProducts,
          orderMode,
          orderType: 'individual',
          customerNumber: safeCustomerNumber,
        });

        const verified = await openRazorpayCheckout({
          paymentData,
          orderTotal: Number(paymentData.calculatedAmount ?? pricingPreview.finalTotal ?? 0),
        });

        paymentSnapshot = {
          ...verified,
          paymentId: paymentData.paymentId,
          razorpay_order_id: paymentData.razorpay_order_id,
          amount: paymentData.amount,
          calculatedAmount: paymentData.calculatedAmount,
        };

        setVerifiedPaymentData(paymentSnapshot);
        setPaidOnline(true);
        setPaymentLoading(false);
      }

      const idempotencyKey = `ord_${vendorId}_${businessType}_${safeCustomerNumber}_${Date.now()}`;
      const resolvedFoodRadarCustomerId = await ensureFoodRadarCustomerProfile();

      const payload = {
        customer_number: safeCustomerNumber,
        customer_name: customerName || null,
        customer_mobile: mobileNumber || null,
        products: orderProducts,
        order_type: 'individual',
        group_size: null,
        family_size: null,
        offer_name:
          pricingPreview.validatedOfferName ||
          selectedSmartOffer?.offer_name ||
          null,
        applied_offer_id:
          pricingPreview.appliedOffer?.id ||
          selectedSmartOffer?.id ||
          bestBillDiscount?.id ||
          null,
        appliedOfferId:
          pricingPreview.appliedOffer?.id ||
          selectedSmartOffer?.id ||
          bestBillDiscount?.id ||
          null,
        discount_amount: Number(pricingPreview.discountAmount || 0),
        vendorId: Number(vendorId),
        vendorType: businessType,
        businessType,
        table: tableNumber || qrName || null,
        tableNumber: tableNumber || qrName || null,
        table_number: tableNumber || qrName || null,
        qrName: qrName || null,
        qr_name: qrName || null,
        qrToken: qrToken || null,
        qr_token: qrToken || null,
        order_mode: orderMode,
        customer_note: String(orderNote || '').trim() || null,
        order_note: String(orderNote || '').trim() || null,
        special_instructions: String(orderNote || '').trim() || null,
        online_source: 'qr',
        order_source: 'qr',
        source: 'qr',
        deviceKey: getCustomerPushDeviceKey(),
        device_key: getCustomerPushDeviceKey(),
        foodRadarDeviceKey: getCustomerPushDeviceKey(),
        food_radar_device_key: getCustomerPushDeviceKey(),
        foodRadarCustomerId: resolvedFoodRadarCustomerId,
        food_radar_customer_id: resolvedFoodRadarCustomerId,
        customer_id: resolvedFoodRadarCustomerId,
        delivery_details: buildDeliveryDetailsPayload(),
        paymentReferenceId: paymentSnapshot?.paymentReferenceId || paymentSnapshot?.paymentId || null,
        idempotencyKey,
      };

      const response = await axios.post(`${apiUrl}/public/order/place`, payload);

      const orderId =
        response.data?.orderId ||
        response.data?.insertId ||
        response.data?.order?.id ||
        response.data?.id;

      const fallbackToken = generateToken();
      const fallbackRedeemCode = generateRedeemCode();
      const expiryDate = getExpiryDateString();

      let redeemPayload = {
        token: fallbackToken,
        code: fallbackRedeemCode,
        redeemCode: fallbackRedeemCode,
        expiry_date: expiryDate,
        expiryDate,
      };

      try {
        const redeemRes = await saveRedeemCode({
          orderId,
          token: fallbackToken,
          redeemCode: fallbackRedeemCode,
          expiryDate,
        });

        redeemPayload = {
          ...redeemPayload,
          ...redeemRes,
          token: redeemRes?.token || fallbackToken,
          code: redeemRes?.code || redeemRes?.redeemCode || fallbackRedeemCode,
          redeemCode: redeemRes?.redeemCode || redeemRes?.code || fallbackRedeemCode,
          expiry_date: redeemRes?.expiry_date || redeemRes?.expiryDate || expiryDate,
          expiryDate: redeemRes?.expiryDate || redeemRes?.expiry_date || expiryDate,
        };
      } catch {
        // Fallback redeem details still shown to customer.
      }

      const finalOrder = {
        ...(response.data?.order || {}),
        id: orderId,
        order_id: orderId,
        products: orderProducts,
        productsParsed: orderProducts,
        customer_number: safeCustomerNumber,
        customer_name: customerName || null,
        customer_mobile: safeCustomerNumber || null,
        total_price: Number(response.data?.totalPrice ?? pricingPreview.finalTotal ?? 0),
        order_mode: orderMode,
        table_number: orderMode === 'dine_in' ? (tableNumber || qrName || null) : null,
        status: 'pending',
        payment_status: paymentSnapshot ? 'paid' : orderMode === 'delivery' ? 'cod' : 'unpaid',
        payment_method: paymentSnapshot ? 'razorpay' : orderMode === 'delivery' ? 'cash' : 'none',
        payment_reference_id: paymentSnapshot?.paymentReferenceId || null,
        redeem: {
          token: redeemPayload.token,
          code: redeemPayload.code || redeemPayload.redeemCode,
          redeemCode: redeemPayload.redeemCode || redeemPayload.code,
          expiry_date: redeemPayload.expiry_date || redeemPayload.expiryDate,
        },
        token_number: redeemPayload.token,
        redeem_code: redeemPayload.code || redeemPayload.redeemCode,
        expiry_date: redeemPayload.expiry_date || redeemPayload.expiryDate,
        food_radar_customer_id:
          response.data?.order?.food_radar_customer_id ||
          response.data?.food_radar_customer_id ||
          resolvedFoodRadarCustomerId ||
          null,
        coins_earned:
          Number(
            response.data?.order?.coins_earned ??
              response.data?.coins_earned ??
              response.data?.order?.coinsEarned ??
              0
          ) || 0,
        coin_earn_status:
          response.data?.order?.coin_earn_status ||
          response.data?.coin_earn_status ||
          'none',
        logs: [
          {
            id: 'new-order-pending',
            status: 'pending',
            new_status: 'pending',
            note: 'Order placed from QR',
            created_at: 'Just now',
          },
        ],
      };

      setPlacedOrder(finalOrder);
      setStep(STEP_SUCCESS);

      // Order response se pehle pending coins wallet me save ho chuke hote hain.
      // Menu par wapas aane se pehle current vendor ka latest wallet refresh karo.
      if (resolvedFoodRadarCustomerId) {
        fetchQrCoinWallet(resolvedFoodRadarCustomerId);
      }

      const earnedCoins = Number(finalOrder.coins_earned || 0);

      if (earnedCoins > 0) {
        setWonCoins(earnedCoins);
        setShowCoinWinPopup(true);
        window.setTimeout(() => {
          setShowCoinWinPopup(false);
        }, 3200);
      }

      setMessage('Order placed successfully.');
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Order place failed.';

      setErrorMessage(msg);
    } finally {
      setPlacingOrder(false);
      setPaymentLoading(false);
      setIsPaymentVerifying(false);
      setPaymentStatusMessage('');
    }
  };

  const resetOrderFlow = () => {
    pricingPreviewRequestRef.current += 1;
    setCart([]);
    setOrderNote('');
    setPricingPreview({
      products: [],
      baseTotal: 0,
      deliveryCharge: 0,
      packagingCharge: 0,
      extraCharges: 0,
      discountAmount: 0,
      finalTotal: 0,
      validatedOfferName: null,
      appliedOffer: null,
      deliveryEtaMinutes: null,
    });
    setPricingPreviewReady(false);
    setPricingPreviewError('');
    setPlacedOrder(null);
    setShowCoinWinPopup(false);
    setShowCoinClaimPopup(false);
    setWonCoins(0);
    setPaidOnline(false);
    setVerifiedPaymentData(null);
    setMessage('');
    setErrorMessage('');
    setNotificationMessage('');
    setNotificationTokenSaved(false);
    setStep(STEP_PRODUCTS);
  };

  const goToCustomerFromMode = () => {
    if (!validateModeStep()) return;
    if (!validateCartBeforeReview()) return;
    clearMessages();
    setStep(STEP_CUSTOMER);
  };

  const goToProductsFromCustomer = () => {
    if (!validateCustomerStep()) return;
    if (!validatePaymentStep()) return;
    clearMessages();
    setStep(STEP_PAYMENT);
  };

  const goToReviewFromProducts = () => {
    if (!validateModeStep()) return;
    if (!validateCartBeforeReview()) return;
    clearMessages();
    setStep(STEP_REVIEW);
  };

  const goToPaymentFromReview = () => {
    if (!validateCartBeforeReview()) return;
    if (!validateModeStep()) return;
    if (previewLoading || !pricingPreviewReady) {
      setErrorMessage(
        pricingPreviewError ||
          'Final price verify ho raha hai. Please thoda wait karke dobara Continue karein.'
      );
      return;
    }
    clearMessages();
    setStep(STEP_CUSTOMER);
  };

  const toggleDescription = (productId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const getOrderProducts = (order) => {
    return safeParseProducts(
      order?.productsParsed ||
        order?.products ||
        order?.items ||
        order?.orderedProducts ||
        []
    );
  };

  const getOrderToken = (order) => {
    return (
      order?.redeem?.token ||
      order?.token_number ||
      order?.tokenNumber ||
      order?.token ||
      '-'
    );
  };

  const getOrderRedeemCode = (order) => {
    return (
      order?.redeem?.code ||
      order?.redeem?.redeemCode ||
      order?.redeem_code ||
      order?.redeemCode ||
      order?.code ||
      '-'
    );
  };

  const getOrderExpiry = (order) => {
    return (
      order?.redeem?.expiry_date ||
      order?.redeem?.expiryDate ||
      order?.expiry_date ||
      order?.expiryDate ||
      '-'
    );
  };

  const getOrderTotal = (order) => {
    return Number(order?.total_price || order?.totalPrice || order?.final_total || 0);
  };

  const getOrderModeLabel = (value) => {
    if (value === 'dine_in') return 'Dine In';
    if (value === 'takeaway') return 'Takeaway';
    if (value === 'delivery') return 'Delivery';
    return normalizeStatusText(value);
  };

  const handleLandingNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      setNotificationPermission('unsupported');
      setShowLandingNotificationPrompt(false);
      return;
    }

    if (notificationTokenSaving) return;

    try {
      setNotificationTokenSaving(true);
      setNotificationMessage('');

      let permission = Notification.permission;
      const previousPermission = permission;

      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      setNotificationPermission(permission);
      setShowLandingNotificationPrompt(false);

      if (permission !== 'granted') {
        return;
      }

      // Permission milte hi customer SW register kar do.
      // FCM token DB sync customer ID milte hi existing auto-sync effect karega.
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.register(
            CUSTOMER_SERVICE_WORKER_PATH,
            {
              scope: CUSTOMER_SERVICE_WORKER_SCOPE,
              updateViaCache: 'none',
            }
          );
        } catch (error) {
          console.warn(
            '[CUSTOMER FCM] Landing SW registration skipped:',
            error?.message || error
          );
        }
      }

      // Known customer ho ya anonymous QR device, permission milte hi
      // current device ka FCM token DB me sync/upsert kar do.
      const existingCustomerId = Number(
        foodRadarCustomerId ||
          localStorage.getItem('foodRadarCustomerId') ||
          0
      ) || null;

      await getAndSaveCustomerFcmToken({
        customerId: existingCustomerId,
      });

      if (
        previousPermission !== 'granted' &&
        permission === 'granted'
      ) {
        await recordCustomerVendorTouch({
          eventType: 'notification_allowed',
          customerId: existingCustomerId,
        });
      }
    } catch (error) {
      console.warn(
        '[CUSTOMER FCM] Landing permission flow:',
        error?.message || error
      );
    } finally {
      setNotificationTokenSaving(false);
    }
  };


  const renderMessageBlock = () => (
    <div className="grocery-message-shell grocery-qr-container">
      {message ? <div className="grocery-message grocery-compact-msg">{message}</div> : null}
      {errorMessage ? <div className="grocery-error grocery-compact-msg">{errorMessage}</div> : null}
      {paymentStatusMessage ? <div className="grocery-message grocery-compact-msg">{paymentStatusMessage}</div> : null}
    </div>
  );

  const renderLandingStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-landing-card grocery-landing-v2">
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div className="grocery-chip-row" style={{ flex: 1 }}>
            <span className="grocery-chip">🍔 Fast Food QR</span>
            <span className="grocery-chip">{shopStatus.label}</span>
            <span className="grocery-chip">{getShopTimingText(orderSettings)}</span>
            {qrHasDisplayLocation ? (
              <span className="grocery-chip">
                {tableNumber ? `Table ${tableNumber}` : qrDisplayLabel}
              </span>
            ) : null}
          </div>

          <div
            title={
              notificationPermission === 'granted'
                ? 'Notifications allowed'
                : notificationPermission === 'denied'
                  ? 'Notifications blocked'
                  : 'Notification permission pending'
            }
            aria-label={
              notificationPermission === 'granted'
                ? 'Notifications allowed'
                : notificationPermission === 'denied'
                  ? 'Notifications blocked'
                  : 'Notification permission pending'
            }
            style={{
              width: 38,
              height: 38,
              minWidth: 38,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 19,
              background:
                notificationPermission === 'granted'
                  ? 'rgba(34,197,94,0.16)'
                  : notificationPermission === 'denied'
                    ? 'rgba(239,68,68,0.12)'
                    : 'rgba(245,158,11,0.14)',
              border:
                notificationPermission === 'granted'
                  ? '1px solid rgba(34,197,94,0.55)'
                  : notificationPermission === 'denied'
                    ? '1px solid rgba(239,68,68,0.45)'
                    : '1px solid rgba(245,158,11,0.45)',
              boxShadow:
                notificationPermission === 'granted'
                  ? '0 0 0 4px rgba(34,197,94,0.07)'
                  : 'none',
            }}
          >
            {notificationTokenSaving
              ? '⏳'
              : notificationPermission === 'granted'
                ? '🔔'
                : notificationPermission === 'denied'
                  ? '🔕'
                  : '🔔'}
          </div>
        </div>

        <div className="grocery-landing-shop">
          <div className="grocery-landing-logo">
            {shopImage ? (
              <img src={normalizeImage(shopImage)} alt={shopName} />
            ) : (
              <span>🍔</span>
            )}
          </div>

          <div>
            <h2>{shopName}</h2>
            <p>
              QR scan karke fast food order karein. Mode choose karein, details fill karein,
              products add karein aur token/redeem code lein.
            </p>
          </div>
        </div>

        <div className="grocery-landing-action-grid">
          <button
            type="button"
            className="grocery-big-action-btn booking"
            onClick={() => {
              clearMessages();
              setStep(STEP_MODE);
            }}
          >
            <span>🍟</span>
            <div>
              <strong>Book Now</strong>
              <small>Dine-in, takeaway ya delivery order place karein.</small>
            </div>
          </button>

          <button
            type="button"
            className="grocery-big-action-btn track"
            onClick={() => {
              clearMessages();
              setTrackMobile(normalizeMobile(mobileNumber));
              setStep(STEP_TRACK);
            }}
          >
            <span>📍</span>
            <div>
              <strong>Track Your Order</strong>
              <small>Mobile number se token, redeem code aur status dekhein.</small>
            </div>
          </button>
        </div>

        <div className="grocery-landing-note">
          {shopStatus.message}
          {shopLocation ? (
            <>
              <br />📍 {shopLocation}
            </>
          ) : null}
        </div>
      </div>

      {showLandingNotificationPrompt &&
      notificationPermission === 'default' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enable notifications"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(10, 15, 25, 0.62)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
          }}
          onClick={() => setShowLandingNotificationPrompt(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 390,
              background: '#ffffff',
              color: '#111827',
              borderRadius: 22,
              padding: '24px 20px 18px',
              boxShadow: '0 24px 70px rgba(0,0,0,0.28)',
              position: 'relative',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close notification popup"
              onClick={() => setShowLandingNotificationPrompt(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                color: '#4b5563',
                fontSize: 20,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              ×
            </button>

            <div
              style={{
                width: 64,
                height: 64,
                margin: '0 auto 14px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 31,
                background: 'rgba(34,197,94,0.12)',
                border: '1px solid rgba(34,197,94,0.28)',
              }}
            >
              🔔
            </div>

            <h3
              style={{
                margin: '0 30px 8px',
                textAlign: 'center',
                fontSize: 21,
                lineHeight: 1.25,
              }}
            >
              Order Updates Miss Mat Karein
            </h3>

            <p
              style={{
                margin: '0 auto 18px',
                maxWidth: 320,
                textAlign: 'center',
                color: '#6b7280',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              Order status aur VAKRA Food Radar ke useful offers ke liye
              notifications allow karein.
            </p>

            <button
              type="button"
              onClick={handleLandingNotificationPermission}
              disabled={notificationTokenSaving}
              style={{
                width: '100%',
                minHeight: 48,
                border: 0,
                borderRadius: 14,
                background: '#16a34a',
                color: '#ffffff',
                fontSize: 16,
                fontWeight: 800,
                cursor: notificationTokenSaving ? 'default' : 'pointer',
                opacity: notificationTokenSaving ? 0.72 : 1,
              }}
            >
              {notificationTokenSaving
                ? 'Please wait...'
                : 'Allow Notifications'}
            </button>

            <button
              type="button"
              onClick={() => setShowLandingNotificationPrompt(false)}
              style={{
                width: '100%',
                minHeight: 42,
                marginTop: 8,
                border: 0,
                background: 'transparent',
                color: '#6b7280',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Abhi nahi
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderModeStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-card grocery-step-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Step 3</span>
            <h2 className="grocery-section-title">Order Mode Select Karein</h2>
            <p className="grocery-muted">Grocery flow jaisa simple mode selection.</p>
          </div>

          <button type="button" className="grocery-mini-link-btn" onClick={() => setStep(STEP_REVIEW)}>
            Back
          </button>
        </div>

        <div className="grocery-mode-grid fastfood-mode-grid">
          <button
            type="button"
            className={`grocery-mode-btn ${orderMode === 'dine_in' ? 'active' : ''}`}
            disabled={!dineInEnabled}
            onClick={() => setOrderMode('dine_in')}
          >
            🍽️ Dine In
            <small>{qrHasDisplayLocation ? (tableNumber ? `Table ${tableNumber}` : qrDisplayLabel) : 'Restaurant me order'}</small>
          </button>

          <button
            type="button"
            className={`grocery-mode-btn ${orderMode === 'takeaway' ? 'active' : ''}`}
            disabled={!pickupEnabled}
            onClick={() => setOrderMode('takeaway')}
          >
            🛍️ Takeaway
            <small>{qrHasDisplayLocation ? qrDisplayLabel : 'Counter se order pickup karein'}</small>
          </button>

          <button
            type="button"
            className={`grocery-mode-btn ${orderMode === 'delivery' ? 'active' : ''}`}
            disabled={!deliveryEnabled}
            onClick={() => setOrderMode('delivery')}
          >
            🛵 Delivery
            <small>
              {deliveryEnabled
                ? qrHasDisplayLocation
                  ? `${qrDisplayLabel} • Fee ${formatMoney(orderSettings.delivery_fee)}`
                  : `Fee ${formatMoney(orderSettings.delivery_fee)}`
                : 'Not available'}
            </small>
          </button>
        </div>

        <div className="grocery-mode-info-grid">
          <div>
            <span>Shop Timing</span>
            <strong>{getShopTimingText(orderSettings)}</strong>
          </div>

          <div>
            <span>Minimum Order</span>
            <strong>{formatMoney(currentMinOrder)}</strong>
          </div>

          <div>
            <span>Packaging Fee</span>
            <strong>{formatMoney(orderSettings.packaging_fee)}</strong>
          </div>

          <div>
            <span>Delivery ETA</span>
            <strong>
              {orderSettings.delivery_eta_min || orderSettings.delivery_eta_max
                ? `${orderSettings.delivery_eta_min}-${orderSettings.delivery_eta_max} min`
                : '-'}
            </strong>
          </div>

          <div>
            <span>Delivery Radius</span>
            <strong>{deliveryRadiusKm > 0 ? `${deliveryRadiusKm} KM` : 'Not set'}</strong>
          </div>
        </div>

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary" type="button" onClick={() => setStep(STEP_REVIEW)}>
            Back
          </button>

          <button className="grocery-action-button" type="button" onClick={goToCustomerFromMode}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );

  const renderReviewNotificationPrompt = () => {
    if (
      step !== STEP_REVIEW ||
      !showLandingNotificationPrompt ||
      notificationPermission !== 'default'
    ) {
      return null;
    }

    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Enable notifications"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: 'rgba(10, 15, 25, 0.62)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 18,
        }}
        onClick={() => setShowLandingNotificationPrompt(false)}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 390,
            background: '#ffffff',
            color: '#111827',
            borderRadius: 22,
            padding: '24px 20px 18px',
            boxShadow: '0 24px 70px rgba(0,0,0,0.28)',
            position: 'relative',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Close notification popup"
            onClick={() => setShowLandingNotificationPrompt(false)}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 34,
              height: 34,
              borderRadius: '50%',
              border: '1px solid #e5e7eb',
              background: '#f9fafb',
              color: '#4b5563',
              fontSize: 20,
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <div style={{ textAlign: 'center', fontSize: 42, marginBottom: 10 }}>🔔</div>
          <h3 style={{ margin: '0 30px 8px', textAlign: 'center', fontSize: 21 }}>
            Order Updates Miss Mat Karein
          </h3>
          <p style={{ margin: '0 auto 18px', textAlign: 'center', color: '#6b7280', fontSize: 14, lineHeight: 1.5 }}>
            Order status aur VAKRA Food Radar ke useful offers ke liye notifications allow karein.
          </p>

          <button
            type="button"
            onClick={handleLandingNotificationPermission}
            disabled={notificationTokenSaving}
            style={{
              width: '100%',
              minHeight: 48,
              border: 0,
              borderRadius: 14,
              background: '#16a34a',
              color: '#ffffff',
              fontSize: 16,
              fontWeight: 800,
              cursor: notificationTokenSaving ? 'default' : 'pointer',
              opacity: notificationTokenSaving ? 0.72 : 1,
            }}
          >
            {notificationTokenSaving ? 'Please wait...' : 'Allow Notifications'}
          </button>

          <button
            type="button"
            onClick={() => setShowLandingNotificationPrompt(false)}
            style={{
              width: '100%',
              minHeight: 42,
              marginTop: 8,
              border: 0,
              background: 'transparent',
              color: '#6b7280',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Abhi nahi
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const renderCustomerStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-card grocery-step-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Step 3</span>
            <h2 className="grocery-section-title">Customer Details</h2>
            <p className="grocery-muted">
              {orderMode === 'delivery'
                ? 'Delivery ke liye name, mobile aur address required hai.'
                : orderMode === 'takeaway'
                  ? 'Takeaway ke liye name aur mobile number required hai.'
                  : 'Name/mobile optional hai. Online payment ke liye mobile required hai.'}
            </p>
          </div>

          <button type="button" className="grocery-mini-link-btn" onClick={() => setStep(STEP_REVIEW)}>
            Back
          </button>
        </div>

        <div className="grocery-customer-tip">
          Selected Mode: <strong>{getOrderModeLabel(orderMode)}</strong>
          {qrHasDisplayLocation ? <> • {orderMode === 'dine_in' && tableNumber ? `Table ${tableNumber}` : qrDisplayLabel}</> : null}
        </div>

        {savedDeviceProfile && savedDeviceProfileApplied && !isEditingCustomerDetails ? (
          <div className="saved-address-card compact">
            <div className="saved-address-summary-head">
              <span>📍 Customer Details</span>
              <button
                type="button"
                className="saved-address-edit-btn"
                aria-label="Edit customer details"
                title="Edit customer details"
                onClick={() => setIsEditingCustomerDetails(true)}
              >
                ✎
              </button>
            </div>

            <div className="saved-customer-row">
              <strong>{customerName || savedDeviceProfile.customer_name || 'Customer'}</strong>
              {mobileNumber || savedDeviceProfile.customer_mobile ? (
                <span>{mobileNumber || savedDeviceProfile.customer_mobile}</span>
              ) : null}
            </div>

            {orderMode === 'delivery' ? (
              <>
                <p className="saved-address-summary-text">
                  {[
                    addressLine1,
                    addressLine2,
                    landmark,
                    city,
                    stateName,
                    pincode,
                  ].filter(Boolean).join(', ')}
                </p>

                <button
                  type="button"
                  className="saved-address-work-btn"
                  onClick={() => {
                    setIsEditingCustomerDetails(true);
                    handleUseCustomerLocation(true);
                  }}
                  disabled={customerLocationLoading}
                >
                  {customerLocationLoading ? 'Location Detecting...' : '＋ Add Work Place'}
                </button>
              </>
            ) : null}
          </div>
        ) : (
          <>
        <div className="grocery-two-col-grid customer-mobile-first-grid">
          <div className="grocery-form-group">
            <label>Mobile Number {orderMode === 'delivery' || orderMode === 'takeaway' || paymentMode === 'online' ? '*' : ''}</label>
            <input
              value={mobileNumber}
              onChange={(e) => setMobileNumber(normalizeMobile(e.target.value))}
              placeholder="10 digit mobile number"
              inputMode="numeric"
              autoComplete="tel"
            />
          </div>

          <div className="grocery-form-group">
            <label>Customer Name {orderMode === 'delivery' || orderMode === 'takeaway' ? '*' : ''}</label>
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter customer name"
              autoComplete="name"
            />
          </div>
        </div>

        {orderMode === 'delivery' ? (
          <div className="grocery-delivery-fields">
            <div className="delivery-location-panel">
              <div className="delivery-location-head">
                <div>
                  <strong>📍 Delivery Location</strong>
                  <small>
                    {deliveryRadiusKm > 0
                      ? `Shop delivery radius: ${deliveryRadiusKm} KM`
                      : 'Shop ne delivery radius set nahi kiya hai.'}
                  </small>
                </div>

                <button
                  type="button"
                  className="delivery-location-btn"
                  onClick={() => handleUseCustomerLocation(true)}
                  disabled={customerLocationLoading}
                >
                  {customerLocationLoading ? 'Detecting...' : 'Add Work Place'}
                </button>
              </div>

              {customerLocationMessage ? (
                <div
                  className={`delivery-location-message ${
                    deliveryRangeStatus.checked && deliveryRangeStatus.allowed
                      ? 'success'
                      : deliveryRangeStatus.checked && !deliveryRangeStatus.allowed
                        ? 'danger'
                        : ''
                  }`}
                >
                  {customerLocationMessage}
                </div>
              ) : null}

              {deliveryRangeStatus.message ? (
                <div
                  className={`delivery-range-status ${
                    deliveryRangeStatus.allowed ? 'success' : 'danger'
                  }`}
                >
                  {deliveryRangeStatus.message}
                </div>
              ) : null}

              {hasCustomerCoordinates ? (
                <div className="delivery-location-meta">
                  <span>Lat: {customerLatitude}</span>
                  <span>Lng: {customerLongitude}</span>
                  {customerLocationAccuracy ? (
                    <span>Accuracy: {Math.round(customerLocationAccuracy)}m</span>
                  ) : null}
                  {deliveryRangeStatus.distanceKm !== null ? (
                    <span>Distance: {deliveryRangeStatus.distanceKm.toFixed(2)} KM</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="grocery-two-col-grid">
              <div className="grocery-form-group">
                <label>Address Line 1 *</label>
                <input
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="House / Flat / Street"
                />
              </div>

              <div className="grocery-form-group">
                <label>Address Line 2</label>
                <input
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Area / Locality"
                />
              </div>

              <div className="grocery-form-group">
                <label>Landmark</label>
                <input
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  placeholder="Nearby landmark"
                />
              </div>

              <div className="grocery-form-group">
                <label>City *</label>
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
              </div>

              <div className="grocery-form-group">
                <label>State *</label>
                <input value={stateName} onChange={(e) => setStateName(e.target.value)} placeholder="State" />
              </div>

              <div className="grocery-form-group">
                <label>Pincode *</label>
                <input
                  value={pincode}
                  onChange={(e) => setPincode(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                  placeholder="6 digit pincode"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="grocery-form-group">
              <label>Delivery Notes</label>
              <textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder="Floor, gate, instruction etc."
                rows={3}
              />
            </div>

          </div>
        ) : null}
          </>
        )}

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary" type="button" onClick={() => setStep(STEP_REVIEW)}>
            Back
          </button>

          <button
            className="grocery-action-button"
            type="button"
            onClick={goToProductsFromCustomer}
            disabled={
              customerLocationLoading ||
              (orderMode === 'delivery' &&
                deliveryRadiusKm > 0 &&
                hasCustomerCoordinates &&
                !deliveryRangeStatus.allowed)
            }
          >
            Continue to Payment
          </button>
        </div>
      </div>
    </div>
  );

  const renderProductCard = (product) => {
    const cartItem = getCartItem(product.id);
    const qty = Number(cartItem?.quantity || 0);
    const unavailable = isUnavailable(product);
    const foodBadge = getFoodBadge(product.food_type);
    const expanded = expandedDescriptions[product.id];

    return (
      <div
        key={product.id}
        className={`grocery-slider-card fastfood-product-card ${qty > 0 ? 'selected' : ''} ${unavailable ? 'unavailable' : ''}`}
      >
        <div className="grocery-slider-image-wrap">
          <img src={product.image_url} alt={product.name} />

          {Number(product.trend_item || 0) === 1 ? (
            <span className="grocery-mini-badge">🔥 Trend</span>
          ) : null}

          {qty > 0 ? <span className="grocery-check-badge">✓</span> : null}
          {unavailable ? <div className="grocery-unavailable-layer">Unavailable</div> : null}
        </div>

        <div className="grocery-slider-body">
          <div>
            <div className="grocery-slider-name">{product.name}</div>

            <div className="grocery-product-tags-row">
              <span className={`grocery-food-badge ${foodBadge.className}`}>{foodBadge.label}</span>
              <span className="grocery-category-badge">{product.category || 'Others'}</span>
            </div>

            {product.description ? (
              <p className="grocery-product-desc">
                {expanded ? product.description : getShortDescription(product.description, 8)}
                {shouldShowReadMore(product.description, 8) ? (
                  <button type="button" onClick={() => toggleDescription(product.id)}>
                    {expanded ? ' Show Less' : ' Read More'}
                  </button>
                ) : null}
              </p>
            ) : (
              <p className="grocery-product-desc">Fresh and tasty item.</p>
            )}

            <div className="grocery-slider-price-row">
              <span className="grocery-slider-price">{renderPublicPrice(product)}</span>
            </div>
          </div>

          <div className="grocery-slider-action">
            {qty > 0 ? (
              <div className="grocery-qty-controls">
                <button type="button" className="grocery-qty-btn" onClick={() => decreaseQty(product)}>
                  -
                </button>
                <span className="grocery-qty-display">{qty}</span>
                <button
                  type="button"
                  className="grocery-qty-btn active"
                  onClick={() => increaseQty(product)}
                  disabled={unavailable}
                >
                  +
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="grocery-add-btn"
                onClick={() => addProductToCart(product)}
                disabled={unavailable}
              >
                {unavailable ? 'Unavailable' : 'Add'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTodayOffers = () => {
    if (offersLoading) return null;

    if (!normalizedOffers.length) return null;
    if (!showOffersPanel) return null;

    return (
      <div className={`grocery-card fastfood-offers-card fastfood-offers-modern ${showOffersPanel ? 'open' : ''}`}>
        <div className="fastfood-offer-teaser">
          {showOffersPanel ? (
          <div className="fastfood-offer-teaser-left">
            <div className="fastfood-offer-gift-icon">🎁</div>

            <div>
              <div className="fastfood-offer-title-row">
                <h3 className="grocery-section-title">Today’s Offers</h3>
                <span className="fastfood-offer-count-chip">{normalizedOffers.length} offers</span>
              </div>

              <p className="grocery-muted">
                Button dabao aur saare active offers — combo, add-on, discount, Buy X Get Y, Happy Hour, progressive rewards aur coins — dekho.
              </p>

            </div>
          </div>
          ) : null}

        </div>

        {showOffersPanel ? (
          <div className="fastfood-offer-expanded-panel">
            <div className="fastfood-offer-expanded-head">
              <div>
                <strong>Available Deals</strong>
                <small>Combo add kar sakte hain. Discount review me auto apply hoga.</small>
              </div>

              <button type="button" onClick={() => setShowOffersPanel(false)}>
                Close
              </button>
            </div>

            <div className="fastfood-offer-slider">
              {comboOffers.map((offer) => {
                const actualPrice = getComboActualPrice(offer);
                const comboPrice = Number(offer.combo_price || 0);
                const saveAmount = Math.max(0, actualPrice - comboPrice);
                const items = getOfferComboItems(offer);

                return (
                  <div className="fastfood-offer-card combo" key={`combo-${offer.id}`}>
                    <div className="fastfood-offer-card-top">
                      <span className="fastfood-offer-type">{getOfferTypeLabel(offer.offer_type)}</span>
                      <span className="fastfood-offer-emoji">🍔</span>
                    </div>

                    <div className="fastfood-offer-title">{offer.offer_name || 'Combo Offer'}</div>

                    <p>
                      {items.map((item) => item.name).slice(0, 3).join(' + ') ||
                        offer.offer_text ||
                        'Combo items'}
                    </p>

                    <div className="fastfood-offer-price-line">
                      {actualPrice > comboPrice ? <del>{formatMoney(actualPrice)}</del> : null}
                      <strong>{formatMoney(comboPrice)}</strong>
                      {saveAmount > 0 ? <em>Save {formatMoney(saveAmount)}</em> : null}
                    </div>

                    <button type="button" onClick={() => addComboOfferToCart(offer)}>
                      Add Combo
                    </button>
                  </div>
                );
              })}

              {addonOffers.map((offer) => {
                const product = findProductById(offer.addon_product_id);
                const unlocked = isAddonUnlocked(offer);
                const minBill = Number(offer.min_bill_amount || offer.min_purchasing_price || 0);
                const required = Math.max(0, minBill - normalCartSubtotal);
                const original = Number(offer.addon_original_price || product?.selling_price || product?.price || 0);
                const special = Number(offer.addon_special_price || 0);

                return (
                  <div className={`fastfood-offer-card addon ${unlocked ? 'unlocked' : 'locked'}`} key={`addon-${offer.id}`}>
                    <div className="fastfood-offer-card-top">
                      <span className="fastfood-offer-type addon">{getOfferTypeLabel(offer.offer_type)}</span>
                      <span className="fastfood-offer-emoji">{unlocked ? '🎁' : '🔒'}</span>
                    </div>

                    <div className="fastfood-offer-title">
                      {product?.name || offer.addon_product_name || offer.offer_name || 'Add-on Deal'}
                    </div>

                    <p>Bill {formatMoney(minBill)}+ par unlock</p>

                    <div className="fastfood-offer-price-line">
                      {original > special ? <del>{formatMoney(original)}</del> : null}
                      <strong>{formatMoney(special)}</strong>
                    </div>

                    {unlocked ? (
                      <button type="button" className="success" onClick={() => addAddonOfferToCart(offer)}>
                        Add Deal
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="locked"
                        onClick={() => setMessage(`${formatMoney(required)} aur add karein, phir ye deal unlock hogi.`)}
                      >
                        Add {formatMoney(required)} more 🔒
                      </button>
                    )}
                  </div>
                );
              })}

              {billDiscountOffers.map((offer) => {
                const minBill = Number(offer.min_bill_amount || offer.min_purchasing_price || 0);
                const unlocked = normalCartSubtotal >= minBill;
                const required = Math.max(0, minBill - normalCartSubtotal);
                const discountText = offer.discount_type === 'percent'
                  ? `${Number(offer.discount_value || 0)}% OFF`
                  : `${formatMoney(offer.discount_value)} OFF`;

                return (
                  <div className={`fastfood-offer-card discount ${unlocked ? 'unlocked' : 'locked'}`} key={`discount-${offer.id}`}>
                    <div className="fastfood-offer-card-top">
                      <span className="fastfood-offer-type discount">{getOfferTypeLabel(offer.offer_type)}</span>
                      <span className="fastfood-offer-emoji">🏷️</span>
                    </div>

                    <div className="fastfood-offer-title">{discountText}</div>
                    <p>Bill {formatMoney(minBill)}+ par auto apply</p>

                    {unlocked ? (
                      <strong className="fastfood-offer-unlocked">Applied at Review</strong>
                    ) : (
                      <em className="fastfood-offer-locked-text">Add {formatMoney(required)} more</em>
                    )}
                  </div>
                );
              })}

              {buyXGetYOffers.map((offer) => {
                const buyProductId = Number(offer.buy_product_id || 0);
                const requiredQty = Math.max(1, Number(offer.buy_quantity || 1));
                const boughtQty = activeCartItems
                  .filter(
                    (item) =>
                      !item.offerType &&
                      Number(item.productId || item.id) === buyProductId
                  )
                  .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
                const eligible = boughtQty >= requiredQty;
                const selected = Boolean(getCartItemByKey(`buyxgety_${Number(offer.id)}`));

                return (
                  <div className={`fastfood-offer-card ${eligible ? 'unlocked' : 'locked'}`} key={`buyxgety-${offer.id}`}>
                    <div className="fastfood-offer-card-top">
                      <span className="fastfood-offer-type">{getOfferTypeLabel(offer.offer_type)}</span>
                      <span className="fastfood-offer-emoji">🎁</span>
                    </div>
                    <div className="fastfood-offer-title">{offer.offer_name || offer.offer_text || 'Buy X Get Y'}</div>
                    <p>Buy {requiredQty}, get {Math.max(1, Number(offer.get_quantity || 1))} {String(offer.reward_type || 'free').toLowerCase() === 'free' ? 'FREE' : 'reward'}</p>
                    <button
                      type="button"
                      className={eligible && !selected ? 'success' : 'locked'}
                      disabled={!eligible || selected}
                      onClick={() => addBuyXGetYOfferToCart(offer)}
                    >
                      {selected ? 'Selected' : eligible ? 'Claim Offer' : `Add ${Math.max(0, requiredQty - boughtQty)} more`}
                    </button>
                  </div>
                );
              })}

              {timeSlotOffers.map((offer) => {
                const active = isTimeSlotActiveClient(offer);
                const minBill = Number(offer.min_bill_amount || offer.min_purchasing_price || 0);
                const eligible = active && normalCartSubtotal >= minBill;
                const selected = Number(selectedSmartOfferId) === Number(offer.id);

                return (
                  <div className={`fastfood-offer-card ${eligible ? 'unlocked' : 'locked'}`} key={`timeslot-${offer.id}`}>
                    <div className="fastfood-offer-card-top">
                      <span className="fastfood-offer-type">{getOfferTypeLabel(offer.offer_type)}</span>
                      <span className="fastfood-offer-emoji">⏰</span>
                    </div>
                    <div className="fastfood-offer-title">{offer.offer_name || offer.offer_text || 'Happy Hour'}</div>
                    <p>{String(offer.start_time || '').slice(0, 5)} - {String(offer.end_time || '').slice(0, 5)}</p>
                    <p>{active ? `Minimum bill ${formatMoney(minBill)}` : 'Offer abhi active nahi hai'}</p>
                    <button
                      type="button"
                      className={eligible ? 'success' : 'locked'}
                      disabled={!eligible}
                      onClick={() => setSelectedSmartOfferId(selected ? null : Number(offer.id))}
                    >
                      {selected ? 'Selected' : eligible ? 'Apply Offer' : 'Locked'}
                    </button>
                  </div>
                );
              })}

              {progressiveOffers.map((offer) => {
                const step = getProgressiveUnlockedStep(offer);
                const selected = Number(selectedSmartOfferId) === Number(offer.id);

                return (
                  <div className={`fastfood-offer-card ${step ? 'unlocked' : 'locked'}`} key={`progressive-${offer.id}`}>
                    <div className="fastfood-offer-card-top">
                      <span className="fastfood-offer-type">{getOfferTypeLabel(offer.offer_type)}</span>
                      <span className="fastfood-offer-emoji">📈</span>
                    </div>
                    <div className="fastfood-offer-title">{offer.offer_name || offer.offer_text || 'Progressive Reward'}</div>
                    <p>{step ? `Unlocked: ${step.title || step.reward_product_name || 'Reward'}` : 'Bill badhao aur next reward unlock karo'}</p>
                    <button
                      type="button"
                      className={step ? 'success' : 'locked'}
                      disabled={!step}
                      onClick={() => setSelectedSmartOfferId(selected ? null : Number(offer.id))}
                    >
                      {selected ? 'Selected' : step ? 'Use Reward' : 'Locked'}
                    </button>
                  </div>
                );
              })}

              {coinRewardOffers.map((offer) => (
                <div className={`fastfood-offer-card ${coinEarnPreview.eligible ? 'unlocked' : 'locked'}`} key={`coin-${offer.id}`}>
                  <div className="fastfood-offer-card-top">
                    <span className="fastfood-offer-type">{getOfferTypeLabel(offer.offer_type)}</span>
                    <span className="fastfood-offer-emoji">🪙</span>
                  </div>
                  <div className="fastfood-offer-title">{offer.offer_name || offer.offer_text || 'Earn Shop Coins'}</div>
                  <p>Eligible bill ka {coinEarnPreview.earnPercent}% coins</p>
                  {coinEarnPreview.eligible ? (
                    <strong className="fastfood-offer-unlocked">You can collect approx. {coinEarnPreview.coins} coins</strong>
                  ) : (
                    <em className="fastfood-offer-locked-text">
                      {coinEarnPreview.required > 0
                        ? `Add ${formatMoney(coinEarnPreview.required)} more to earn coins`
                        : 'Coin earning eligibility not met'}
                    </em>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderFloatingOfferHint = () => {
    if (!offerSideHintInfo) return null;

    return (
      <div className={`fastfood-floating-offer-hint ${offerSideHintInfo.type}`}>
        <div className="fastfood-floating-offer-icon">{offerSideHintInfo.icon}</div>
        <div className="fastfood-floating-offer-text">
          <strong>{offerSideHintInfo.title}</strong>
          <span>{offerSideHintInfo.message}</span>
        </div>
        <button
          type="button"
          className="fastfood-floating-offer-close"
          onClick={() => setOfferHintDismissedKey(offerSideHintInfo.key)}
          aria-label="Close offer hint"
        >
          ×
        </button>
      </div>
    );
  };


  const scrollToSearchBox = () => {
    const node = searchShellRef.current;
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        const input = node.querySelector('input');
        if (input) input.focus();
      }, 250);
    }
  };

  const renderFloatingSearchShortcut = () => {
    if (step !== STEP_PRODUCTS || !showFloatingSearchShortcut) return null;

    return (
      <button
        type="button"
        className="fastfood-floating-search-shortcut"
        onClick={scrollToSearchBox}
        aria-label="Open search"
      >
        🔍
      </button>
    );
  };

  const renderBottomCategorySlider = () => {
    if (step !== STEP_PRODUCTS) return null;

    const sliderCategories = ['All', ...categories];

    return (
      <div
        className={`fastfood-bottom-category-drawer ${
          showBottomCategorySlider ? 'open' : ''
        } ${cartCount > 0 ? 'has-cart' : ''}`}
        aria-label="Category filter"
      >
        <div className="fastfood-bottom-category-panel">
          <div className="fastfood-bottom-category-head">
            <div>
              <span className="fastfood-bottom-category-kicker">MENU FILTER</span>
              <strong>
                {categoryFilter || 'All Categories'}
              </strong>
            </div>

            <button
              type="button"
              className="fastfood-bottom-category-close"
              onClick={() => setShowBottomCategorySlider(false)}
              aria-label="Close category filter"
            >
              ×
            </button>
          </div>

          <div className="fastfood-bottom-category-list">
            {sliderCategories.map((category) => {
              const value = category === 'All' ? '' : category;
              const active = categoryFilter === value;

              return (
                <button
                  type="button"
                  key={`bottom-category-${category}`}
                  className={`fastfood-bottom-category-chip ${
                    active ? 'active' : ''
                  }`}
                  onClick={() => {
                    setCategoryFilter(value);
                    setShowBottomCategorySlider(false);
                  }}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="fastfood-bottom-category-handle"
          onClick={() =>
            setShowBottomCategorySlider((current) => !current)
          }
          aria-expanded={showBottomCategorySlider}
          aria-label={
            showBottomCategorySlider
              ? 'Close category filter'
              : 'Open category filter'
          }
        >
          <span className="fastfood-category-chevrons" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="fastfood-category-handle-label">
            {showBottomCategorySlider
              ? 'Close'
              : categoryFilter || 'Categories'}
          </span>
        </button>
      </div>
    );
  };

  const renderMenuModeSelector = () => (
    <div className="grocery-card fastfood-menu-mode-card">
      <div className="grocery-customer-tip">
        Order Mode: <strong>{getOrderModeLabel(orderMode)}</strong>
      </div>

      <div className="grocery-mode-grid fastfood-mode-grid fastfood-menu-mode-grid">
        <button
          type="button"
          className={`grocery-mode-btn ${orderMode === 'delivery' ? 'active' : ''}`}
          disabled={!deliveryEnabled}
          onClick={() => setOrderMode('delivery')}
        >
          🛵 Delivery
          <small>{deliveryEnabled ? 'Home delivery' : 'Not available'}</small>
        </button>

        <button
          type="button"
          className={`grocery-mode-btn ${orderMode === 'takeaway' ? 'active' : ''}`}
          disabled={!pickupEnabled}
          onClick={() => setOrderMode('takeaway')}
        >
          🛍️ Takeaway
          <small>{pickupEnabled ? 'Parcel / Pickup' : 'Not available'}</small>
        </button>

        <button
          type="button"
          className={`grocery-mode-btn ${orderMode === 'dine_in' ? 'active' : ''}`}
          disabled={!dineInEnabled}
          onClick={() => setOrderMode('dine_in')}
        >
          🍽️ Dine In
          <small>
            {dineInEnabled
              ? qrHasDisplayLocation
                ? tableNumber
                  ? `Table ${tableNumber}`
                  : qrDisplayLabel
                : 'Restaurant order'
              : 'Not available'}
          </small>
        </button>
      </div>
    </div>
  );

  const renderQrCoinWalletCard = () => {
    if (!showCoinWalletPanel) return null;

    const availableCoins = Number(qrCoinWallet.availableCoins || 0);
    const pendingCoins = Number(qrCoinWallet.pendingCoins || 0);
    const hasLinkedCustomer = Number(
      foodRadarCustomerId ||
        localStorage.getItem('foodRadarCustomerId') ||
        0
    ) > 0;

    return (
      <div
        className="grocery-card qr-menu-coin-wallet-card"
        style={{
          margin: '10px 0 12px',
          padding: '14px',
          border: '1px solid #f3d37a',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #fff9df 0%, #fff3bd 100%)',
          boxShadow: '0 8px 22px rgba(157, 111, 0, 0.10)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '10px',
          }}
        >
          <div>
            <strong style={{ display: 'block', color: '#5f4300', fontSize: '15px' }}>
              🪙 Your Shop Coins
            </strong>
            <small style={{ color: '#80620d' }}>
              Isi shop ke QR aur Food Radar orders ka balance
            </small>
          </div>

          <button
            type="button"
            className="grocery-mini-link-btn"
            onClick={() => fetchQrCoinWallet()}
            disabled={qrCoinWalletLoading || !hasLinkedCustomer}
            aria-label="Refresh coin balance"
            style={{ minWidth: 'auto', padding: '7px 10px' }}
          >
            {qrCoinWalletLoading ? '...' : '↻'}
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
          }}
        >
          <div
            style={{
              padding: '11px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.82)',
              border: '1px solid rgba(196,145,0,0.18)',
            }}
          >
            <small style={{ display: 'block', color: '#6b7280' }}>Available Coins</small>
            <strong style={{ color: '#087443', fontSize: '22px' }}>
              {qrCoinWalletLoading ? '...' : availableCoins}
            </strong>
          </div>

          <div
            style={{
              padding: '11px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.82)',
              border: '1px solid rgba(196,145,0,0.18)',
            }}
          >
            <small style={{ display: 'block', color: '#6b7280' }}>Pending Coins</small>
            <strong style={{ color: '#b56a00', fontSize: '22px' }}>
              {qrCoinWalletLoading ? '...' : pendingCoins}
            </strong>
          </div>
        </div>

        {!hasLinkedCustomer ? (
          <small style={{ display: 'block', marginTop: '9px', color: '#80620d' }}>
            Eligible order ke baad aapke coins yahan dikhne lagenge.
          </small>
        ) : pendingCoins > 0 ? (
          <small style={{ display: 'block', marginTop: '9px', color: '#80620d' }}>
            Order complete hone ke baad pending coins available ho jayenge.
          </small>
        ) : null}

        {qrCoinWalletError ? (
          <small style={{ display: 'block', marginTop: '8px', color: '#b42318' }}>
            {qrCoinWalletError}
          </small>
        ) : null}
      </div>
    );
  };

  const renderMenuQuickActions = () => (
    <div
      className="qr-menu-quick-actions"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        margin: '10px 0 12px',
      }}
    >
      <button
        type="button"
        className={showOffersPanel ? 'fastfood-show-offer-btn active' : 'fastfood-show-offer-btn'}
        disabled={offersLoading || !normalizedOffers.length}
        onClick={() => {
          setShowOffersPanel((current) => !current);
          setShowCoinWalletPanel(false);
        }}
        style={{ width: '100%', minWidth: 0 }}
      >
        {offersLoading
          ? 'Offers Loading...'
          : showOffersPanel
            ? 'Hide Offers'
            : 'Show Your Offers'}
        <span>{showOffersPanel ? '↑' : '↓'}</span>
      </button>

      <button
        type="button"
        className={showCoinWalletPanel ? 'fastfood-show-offer-btn active' : 'fastfood-show-offer-btn'}
        onClick={() => {
          const willOpen = !showCoinWalletPanel;
          setShowCoinWalletPanel(willOpen);
          setShowOffersPanel(false);

          if (willOpen) {
            fetchQrCoinWallet();
          }
        }}
        style={{ width: '100%', minWidth: 0 }}
      >
        🪙 Your Shop Coins
        <span>{showCoinWalletPanel ? '↑' : '↓'}</span>
      </button>
    </div>
  );

  const renderProductsStep = () => (
    <div className="grocery-qr-container grocery-products-container">
      {renderMenuModeSelector()}

      <div ref={searchShellRef} className="grocery-search-sticky-wrap fastfood-search-offer-sticky-wrap compact-search-only fastfood-search-inline-wrap">
        <div className="grocery-search-topbar fastfood-compact-search-topbar">
          <button
            type="button"
            className="grocery-mini-link-btn"
            onClick={() => {
              clearMessages();
              setTrackMobile(normalizeMobile(mobileNumber));
              setStep(STEP_TRACK);
            }}
          >
            📍 Track Order
          </button>

          <div className="grocery-search-box">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search burger, pizza, fries..."
            />
          </div>

          <button
            type="button"
            className={`fastfood-filter-toggle-btn ${showProductFilters ? 'active' : ''}`}
            onClick={() => setShowProductFilters((prev) => !prev)}
            aria-label="Open filters"
          >
            ☰
          </button>
        </div>

        {showProductFilters ? (
          <div className="fastfood-filter-dropdown-panel">
            <div className="grocery-form-group">
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option value={category} key={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="grocery-form-group">
              <select value={foodTypeFilter} onChange={(e) => setFoodTypeFilter(e.target.value)}>
                <option value="">All Food Type</option>
                <option value="veg">Veg</option>
                <option value="non_veg">Non-Veg</option>
                <option value="egg">Egg</option>
              </select>
            </div>

            <button
              type="button"
              className={`grocery-mini-link-btn ${showTrendingOnly ? 'active' : ''}`}
              onClick={() => setShowTrendingOnly((prev) => !prev)}
            >
              🔥 Trending
            </button>

            <button
              type="button"
              className="grocery-mini-link-btn"
              onClick={() => {
                setSearch('');
                setCategoryFilter('');
                setFoodTypeFilter('');
                setShowTrendingOnly(false);
                setShowProductFilters(false);
              }}
            >
              Clear
            </button>
          </div>
        ) : null}
      </div>

      {renderFloatingSearchShortcut()}
      {renderBottomCategorySlider()}
      {renderFloatingOfferHint()}

      <div className="fastfood-search-sticky-spacer compact" aria-hidden="true" />

      {renderMenuQuickActions()}
      {renderTodayOffers()}
      {renderQrCoinWalletCard()}

      {Object.keys(groupedProducts).length === 0 ? (
        <div className="grocery-empty-state">Product nahi mila.</div>
      ) : (
        Object.entries(groupedProducts).map(([category, products]) => (
          <div className="grocery-card product-section-card" key={category}>
            <div className="grocery-section-head">
              <h3 className="grocery-section-title">{category}</h3>
              <span className="grocery-view-all">{products.length} items</span>
            </div>

            <div className="grocery-horizontal-slider">
              {products.map(renderProductCard)}
            </div>
          </div>
        ))
      )}

      {cartCount > 0 ? (
        <div className="grocery-sticky-bottom-bar">
          <div className="grocery-sticky-total">
            <span>{cartCount} item(s)</span>
            <strong>{formatMoney(cartEstimateTotal)}</strong>
            <small>Extra charges review me dikhenge</small>
          </div>

          <button type="button" className="grocery-sticky-btn" onClick={goToReviewFromProducts}>
            Review Order
          </button>
        </div>
      ) : null}
    </div>
  );

  const renderCartItem = (item, compact = false) => {
    const realProduct = item.offerType ? item : findProductById(item.productId) || item;
    const foodBadge = getFoodBadge(item.food_type || realProduct.food_type);
    const lineTotal = Number(item.selling_price ?? item.price ?? 0) * Number(item.quantity || 0);
    const key = item.cartKey || item.productId;

    return (
      <div className={`grocery-cart-item ${compact ? 'compact' : ''} ${item.offerType ? 'offer-cart-item' : ''}`} key={key}>
        <div className="grocery-cart-thumb">
          <img src={normalizeImage(item.image || realProduct.image_url)} alt={item.name} />
        </div>

        <div className="grocery-cart-content">
          <div className="grocery-product-name">
            {item.offerType === 'combo' ? '🔥 ' : item.offerType === 'bill_unlock_addon' ? '🎁 ' : ''}
            {item.name}
          </div>

          <div className="grocery-product-tags-row">
            {item.offerType ? (
              <span className="grocery-category-badge">{item.offerType === 'combo' ? 'Combo Offer' : 'Unlocked Deal'}</span>
            ) : (
              <span className={`grocery-food-badge ${foodBadge.className}`}>{foodBadge.label}</span>
            )}
            {item.category ? <span className="grocery-category-badge">{item.category}</span> : null}
          </div>

          {item.offerType === 'combo' && safeArray(item.combo_items).length ? (
            <p className="grocery-cart-desc">
              Includes: {safeArray(item.combo_items).map((comboItem) => `${comboItem.name} x${comboItem.quantity}`).join(', ')}
            </p>
          ) : item.description ? (
            <p className="grocery-cart-desc">{item.description}</p>
          ) : null}

          <div className="grocery-meta-line">
            {item.original_price && Number(item.original_price) > Number(item.selling_price || item.price) ? (
              <><del>{formatMoney(item.original_price)}</del> </>
            ) : null}
            {formatMoney(item.selling_price || item.price)} × {item.quantity}
          </div>
        </div>

        <div className="grocery-cart-actions">
          <div className="grocery-qty-controls cart-row-qty">
            <button type="button" className="grocery-qty-btn" onClick={() => decreaseQty(item.offerType ? item : realProduct)}>
              -
            </button>
            <span className="grocery-qty-display">{item.quantity}</span>
            <button type="button" className="grocery-qty-btn active" onClick={() => increaseQty(item.offerType ? item : realProduct)}>
              +
            </button>
          </div>

          {!compact ? (
            <button type="button" className="grocery-remove-btn" onClick={() => removeFromCart(item.cartKey || item.productId)}>
              Remove
            </button>
          ) : null}
        </div>

        <div className="grocery-cart-price">
          {formatMoney(lineTotal)}
        </div>
      </div>
    );
  };

  const getReviewBaseTotal = () => {
    const value = Number(pricingPreview.baseTotal ?? pricingPreview.base_total);
    if (pricingPreviewReady && Number.isFinite(value) && value >= 0) return value;
    return Number(cartEstimateTotal || 0);
  };

  const getReviewExtraCharges = () => {
    const value = Number(pricingPreview.extraCharges ?? pricingPreview.extra_charges);
    return Number.isFinite(value) && value > 0 ? value : 0;
  };

  const getReviewDiscountAmount = () => {
    const value = Number(pricingPreview.discountAmount ?? pricingPreview.discount_amount);
    if (pricingPreviewReady && Number.isFinite(value) && value >= 0) return value;
    return Number(bestBillDiscount?.discountAmount || 0);
  };

  const getReviewFinalTotal = () => {
    const backendFinal = Number(pricingPreview.finalTotal ?? pricingPreview.totalPrice ?? pricingPreview.final_total);

    if (pricingPreviewReady && Number.isFinite(backendFinal) && backendFinal >= 0) {
      return backendFinal;
    }

    return Math.max(0, getReviewBaseTotal() + getReviewExtraCharges() - getReviewDiscountAmount());
  };

  const renderSummaryBox = () => {
    const reviewBaseTotal = getReviewBaseTotal();
    const reviewDiscountAmount = getReviewDiscountAmount();
    const reviewFinalTotal = getReviewFinalTotal();
    const deliveryCharge = pricingPreviewReady
      ? Math.max(0, safeNumber(pricingPreview.deliveryCharge ?? pricingPreview.delivery_charge, 0))
      : orderMode === 'delivery'
        ? Math.max(0, safeNumber(orderSettings.delivery_fee, 0))
        : 0;
    const packagingCharge = pricingPreviewReady
      ? Math.max(0, safeNumber(pricingPreview.packagingCharge ?? pricingPreview.packaging_charge, 0))
      : orderMode !== 'dine_in'
        ? Math.max(0, safeNumber(orderSettings.packaging_fee, 0))
        : 0;

    return (
      <div className="grocery-summary-box">
        <div className="grocery-review-item">
          <span>Subtotal</span>
          <strong>{formatMoney(reviewBaseTotal)}</strong>
        </div>

        {deliveryCharge > 0 ? (
          <div className="grocery-review-item">
            <span>Delivery Charge</span>
            <strong>{formatMoney(deliveryCharge)}</strong>
          </div>
        ) : null}

        {packagingCharge > 0 ? (
          <div className="grocery-review-item">
            <span>Packaging Charge</span>
            <strong>{formatMoney(packagingCharge)}</strong>
          </div>
        ) : null}

        {reviewDiscountAmount > 0 ? (
          <div className="grocery-review-item offer-applied-row">
            <span>Offer Applied</span>
            <strong>-{formatMoney(reviewDiscountAmount)}</strong>
          </div>
        ) : nearestBillDiscount ? (
          <div className="grocery-review-item offer-locked-row">
            <span>Discount Unlock</span>
            <strong>
              Add {formatMoney(Number(nearestBillDiscount.min_bill_amount || nearestBillDiscount.min_purchasing_price || 0) - normalCartSubtotal)} more
            </strong>
          </div>
        ) : null}

        <div className="grocery-review-item grocery-total-row">
          <span>Final Total</span>
          <strong>{formatMoney(reviewFinalTotal)}</strong>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-card grocery-cart-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Step 2</span>
            <h2 className="grocery-section-title">Review Order</h2>
            <p className="grocery-muted">
              Selected products, quantity, offers aur amount verify karein.
            </p>
          </div>

          <button type="button" className="grocery-mini-link-btn" onClick={() => setStep(STEP_PRODUCTS)}>
            Add More
          </button>
        </div>

        <div className="grocery-customer-tip">
          Selected Mode: <strong>{getOrderModeLabel(orderMode)}</strong>
        </div>

        {customerName || mobileNumber ? (
        <div className="grocery-customer-small-summary">
          <div className="grocery-review-item">
            <span>Mode</span>
            <strong>{getOrderModeLabel(orderMode)}</strong>
          </div>

          <div className="grocery-review-item">
            <span>Customer</span>
            <strong>{customerName || '-'}</strong>
          </div>

          <div className="grocery-review-item">
            <span>Mobile</span>
            <strong>{mobileNumber || '-'}</strong>
          </div>
        </div>
        ) : null}

        {orderMode === 'delivery' && addressLine1 ? (
          <div className="grocery-customer-tip">
            Delivery Address: {addressLine1}, {addressLine2 ? `${addressLine2}, ` : ''}
            {landmark ? `${landmark}, ` : ''}
            {city}, {stateName} - {pincode}
          </div>
        ) : null}

        <div className="grocery-cart-list">
          {activeCartItems.map((item) => renderCartItem(item))}
        </div>

        <div className="grocery-order-note-box">
          <label htmlFor="grocery-order-note">
            <strong>Order Note</strong>
            <span> (Optional)</span>
          </label>
          <textarea
            id="grocery-order-note"
            value={orderNote}
            maxLength={500}
            rows={3}
            onChange={(event) => setOrderNote(event.target.value.slice(0, 500))}
            placeholder="Jaise: kam spicy, onion mat dalna, parcel alag pack karein..."
          />
          <small>{orderNote.length}/500</small>
        </div>

        {(activeCartItems.some((item) => item.offerType) || bestBillDiscount) ? (
          <div className="fastfood-applied-offers-box">
            <strong>🎉 Offer Applied</strong>
            {activeCartItems.filter((item) => item.offerType).map((item) => (
              <span key={item.cartKey}>{item.name} added at offer price.</span>
            ))}
            {bestBillDiscount ? (
              <span>{bestBillDiscount.offer_name}: {formatMoney(pricingPreview.discountAmount ?? bestBillDiscount.discountAmount)} discount applied.</span>
            ) : null}
          </div>
        ) : null}

        {previewLoading ? (
          <div className="grocery-message">Price calculating...</div>
        ) : null}

        {!previewLoading && pricingPreviewError ? (
          <div className="grocery-error-message">
            {pricingPreviewError}
            <button type="button" className="grocery-mini-link-btn" onClick={fetchPricingPreview}>
              Retry Price
            </button>
          </div>
        ) : null}

        {renderSummaryBox()}

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary" type="button" onClick={() => setStep(STEP_PRODUCTS)}>
            Back
          </button>

          <button
            className="grocery-action-button"
            type="button"
            onClick={goToPaymentFromReview}
            disabled={previewLoading || !pricingPreviewReady}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );

  const renderPaymentStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-card grocery-step-card grocery-payment-page">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Step 4</span>
            <h2 className="grocery-section-title">Payment</h2>
            <p className="grocery-muted">Payment method choose karein.</p>
          </div>

          <button type="button" className="grocery-mini-link-btn" onClick={() => setStep(STEP_CUSTOMER)}>
            Back
          </button>
        </div>

        <div className="grocery-payment-grid">
          <button
            type="button"
            className={`grocery-payment-card ${paymentMode === 'cash' ? 'active' : ''}`}
            disabled={!codAllowed}
            onClick={() => setPaymentMode('cash')}
          >
            <span>💵</span>
            <strong>{orderMode === 'delivery' ? 'Cash on Delivery' : 'Pay at Counter'}</strong>
            <small>{codAllowed ? 'Order place karein, payment baad me.' : 'Cash disabled hai.'}</small>
          </button>

          <button
            type="button"
            className={`grocery-payment-card ${paymentMode === 'online' ? 'active' : ''}`}
            disabled={!onlineAllowed}
            onClick={() => setPaymentMode('online')}
          >
            <span>💳</span>
            <strong>Online Payment</strong>
            <small>{onlineAllowed ? 'UPI / Card / Netbanking' : 'Online payment disabled hai.'}</small>
          </button>
        </div>

        {renderSummaryBox()}

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary" type="button" onClick={() => setStep(STEP_CUSTOMER)}>
            Back
          </button>

          <button
            className="grocery-action-button"
            type="button"
            onClick={handlePlaceOrder}
            disabled={placingOrder || paymentLoading || isPaymentVerifying || previewLoading || !pricingPreviewReady}
          >
            {placingOrder || paymentLoading || isPaymentVerifying
              ? 'Processing...'
              : paymentMode === 'online'
                ? `Pay ${formatMoney(pricingPreview.finalTotal ?? cartEstimateTotal)}`
                : 'Place Order'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderOrderDetailCard = (order, mode = 'success') => {
    if (!order) return null;

    const products = getOrderProducts(order);
    const isSuccess = mode === 'success';

    return (
      <div className={`grocery-order-detail-card ${isSuccess ? 'success-mode' : 'track-mode'}`}>
        <div className="grocery-order-detail-head">
          <div>
            <h2>{isSuccess ? 'Order Placed Successfully!' : `Order #${order.id || order.order_id || '-'}`}</h2>
            <p>{shopName}</p>
          </div>
          <div className="grocery-order-success-badge">{isSuccess ? '✓' : '📍'}</div>
        </div>

        <div className="grocery-token-redeem-grid">
          <div className="grocery-token-panel">
            <span>Your Token Number</span>
            <strong>{getOrderToken(order)}</strong>
          </div>

          <div className="grocery-redeem-panel">
            <span>Redeem Code</span>
            <strong>{getOrderRedeemCode(order)}</strong>
            <small>Counter par ye code show karein.</small>
          </div>
        </div>

        {Number(order.coins_earned || 0) > 0 ? (
          <div className="qr-coin-claim-card">
            <div className="qr-coin-claim-amount">
              <span className="qr-coin-mini-icon">🪙</span>
              <div>
                <strong>{Number(order.coins_earned)} Coins</strong>
                <small>You won!</small>
              </div>
            </div>

            <button
              type="button"
              className="qr-coin-claim-btn"
              onClick={() => setShowCoinClaimPopup(true)}
            >
              Claim Coins
            </button>
          </div>
        ) : null}

        <div className="grocery-order-info-grid">
          <div>
            <span>Order ID</span>
            <strong>#{order.id || order.order_id || '-'}</strong>
          </div>

          <div>
            <span>Total</span>
            <strong>{formatMoney(getOrderTotal(order))}</strong>
          </div>

          <div>
            <span>Payment</span>
            <strong>{normalizeStatusText(order.payment_status || order.paymentStatus || 'unpaid')}</strong>
          </div>

          <div>
            <span>Method</span>
            <strong>{normalizeStatusText(order.payment_method || order.paymentMethod || 'none')}</strong>
          </div>

          <div>
            <span>Mode</span>
            <strong>{getOrderModeLabel(order.order_mode || order.orderMode)}</strong>
          </div>

          <div>
            <span>Mobile</span>
            <strong>{order.customer_mobile || order.customer_number || '-'}</strong>
          </div>

          <div>
            <span>Expires</span>
            <strong>{getOrderExpiry(order)}</strong>
          </div>

          <div>
            <span>Status</span>
            <strong>{normalizeStatusText(order.status || 'pending')}</strong>
          </div>
        </div>

        {products.length ? (
          <div className="grocery-selected-products-box">
            <h3>Selected Products</h3>
            <div className="grocery-cart-list compact">
              {products.map((item, index) => (
                <div className="grocery-cart-item compact" key={`${item.productId || item.id || item.name}-${index}`}>
                  <div className="grocery-cart-thumb">
                    <img src={normalizeImage(item.image || item.image_url)} alt={item.name || 'Product'} />
                  </div>

                  <div className="grocery-cart-content">
                    <div className="grocery-product-name">{item.name || item.product_name || 'Product'}</div>
                    {item.description ? <p className="grocery-cart-desc">{item.description}</p> : null}
                    <div className="grocery-meta-line">
                      Qty: {item.quantity || item.qty || 0} × {formatMoney(item.price || item.selling_price || 0)}
                    </div>
                  </div>

                  <div className="grocery-cart-price">
                    {formatMoney(item.line_total || Number(item.price || item.selling_price || 0) * Number(item.quantity || item.qty || 0))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {!isSuccess ? renderTrackingTimeline(order) : null}
      </div>
    );
  };

  const renderSuccessStep = () => (
    <div className="grocery-qr-container grocery-success-page">
      {renderOrderDetailCard(placedOrder, 'success')}

      <div className="grocery-actions grocery-inline-actions">
        <button type="button" className="grocery-action-button secondary" onClick={resetOrderFlow}>
          New Order
        </button>

        <button
          type="button"
          className="grocery-action-button"
          onClick={() => {
            setTrackMobile(normalizeMobile(mobileNumber));
            setTodayOrders(placedOrder ? [placedOrder] : []);
            setStep(STEP_TRACK);
          }}
        >
          Track Order
        </button>
      </div>
    </div>
  );

  const renderTrackingTimeline = (order) => {
    const currentStatus = getLatestTrackingStatus(order);
    const activeIndex = getTrackingIndex(currentStatus);
    const displayLogs = buildDisplayLogs(order);
    const isCancelled = currentStatus === 'cancelled';
    const etaState = getOrderEtaState(order, trackingNowMs);

    return (
      <div className={`grocery-tracking-box ${isCancelled ? 'cancelled' : ''}`}>
        <div className="grocery-tracking-head">
          <div>
            <span>Live Order Tracking</span>
            <strong>
              {isCancelled
                ? 'Order Cancelled'
                : TRACKING_STEPS[activeIndex]?.label || 'Order Placed'}
            </strong>
          </div>

          <div className={`grocery-current-status-pill ${isCancelled ? 'cancelled' : ''}`}>
            {isCancelled ? 'Cancelled' : normalizeStatusText(currentStatus)}
          </div>
        </div>

        {etaState && !isCancelled ? (
          <div className={`grocery-tracking-eta ${etaState.expired ? 'expired' : ''}`}>
            <div className="grocery-tracking-eta-icon">⏱️</div>
            <div>
              <span>Estimated Time</span>
              <strong>{etaState.label}</strong>
              <small>{etaState.help}</small>
            </div>
          </div>
        ) : null}

        {isCancelled ? (
          <div className="grocery-cancelled-note">
            Ye order cancel ho gaya hai. Details ke liye shop se contact karein.
          </div>
        ) : (
          <div className="grocery-progress-steps">
            {TRACKING_STEPS.map((trackStep, index) => {
              const isDone = index < activeIndex;
              const isActive = index === activeIndex;

              return (
                <div
                  key={trackStep.key}
                  className={`grocery-progress-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                >
                  <div className="grocery-progress-dot">
                    {isDone ? '✓' : index + 1}
                  </div>

                  <div className="grocery-progress-text">
                    <strong>{trackStep.shortLabel}</strong>
                    <span>{trackStep.help}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="grocery-status-log-list">
          {displayLogs.map((log) => (
            <div key={log.id} className="grocery-status-log-row">
              <div>
                <strong>{log.label}</strong>
                {log.note ? <p>{log.note}</p> : null}
              </div>
              <small>{log.created_at || '-'}</small>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTrackStep = () => (
    <div className="grocery-qr-container grocery-track-page">
      <div className="grocery-card grocery-step-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Track</span>
            <h2 className="grocery-section-title">Track Your Order</h2>
            <p className="grocery-muted">
              Mobile number se order token, redeem code aur live status dekhein.
            </p>
          </div>

          <button
            type="button"
            className="grocery-mini-link-btn"
            onClick={() => {
              if (notificationTrackRequested) {
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  window.location.href = '/food-radar';
                }
                return;
              }

              setStep(STEP_PRODUCTS);
            }}
          >
            {notificationTrackRequested ? 'Back' : 'Back to Menu'}
          </button>
        </div>

        <div className="grocery-track-search-row">
          <div className="grocery-form-group">
            <label>Mobile Number *</label>
            <input
              value={trackMobile}
              onChange={(e) => setTrackMobile(normalizeMobile(e.target.value))}
              placeholder="10 digit mobile"
              inputMode="numeric"
            />
          </div>

          <button
            type="button"
            className="grocery-action-button"
            onClick={() => fetchTodayOrders()}
            disabled={trackLoading}
          >
            {trackLoading ? 'Searching...' : 'Show Orders'}
          </button>
        </div>

        {todayOrders.length ? (
          <div className="grocery-track-list">
            {todayOrders.map((order) => (
              <div key={order.id || order.order_id} className="grocery-track-card">
                {renderOrderDetailCard(order, 'track')}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderVendorContactPopup = () => {
    if (!contactProduct) return null;

    const whatsappNumber = normalizeMobile(vendorNumber || '');
    const messageText = encodeURIComponent(
      `Namaste, mujhe ${contactProduct.name} ka price/detail chahiye.`
    );

    return (
      <div className="grocery-contact-overlay" onClick={() => setContactProduct(null)}>
        <div className="grocery-contact-card" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="grocery-contact-close" onClick={() => setContactProduct(null)}>
            ×
          </button>

          <div className="grocery-contact-head">
            <div className="grocery-contact-icon">☎️</div>
            <div>
              <h3>Contact Vendor</h3>
              <p>{contactProduct.name}</p>
            </div>
          </div>

          <div className="grocery-contact-body">
            <p>Is product ke liye shop se contact karein.</p>
            <strong>{vendorNumber || 'Number not available'}</strong>
          </div>

          <div className="grocery-contact-actions">
            {vendorNumber ? (
              <>
                <a className="grocery-action-button" href={`tel:${vendorNumber}`}>
                  Call
                </a>

                {whatsappNumber ? (
                  <a
                    className="grocery-action-button secondary"
                    href={`https://wa.me/91${whatsappNumber}?text=${messageText}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    WhatsApp
                  </a>
                ) : null}
              </>
            ) : (
              <button className="grocery-action-button secondary" onClick={() => setContactProduct(null)}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };


  const renderCoinWinPopup = () => {
    if (!showCoinWinPopup || wonCoins <= 0 || typeof document === 'undefined') return null;

    const floatingCoins = Array.from({ length: 10 }, (_, index) => index);

    return createPortal(
      <div className="qr-coin-win-overlay" aria-live="polite">
        <div className="qr-coin-win-card">
          <div className="qr-coin-burst" aria-hidden="true">
            {floatingCoins.map((coinIndex) => (
              <span
                key={`coin-burst-${coinIndex}`}
                className={`qr-coin-burst-item burst-${coinIndex + 1}`}
              >
                🪙
              </span>
            ))}
          </div>

          <div className="qr-coin-win-glow" aria-hidden="true" />
          <div className="qr-coin-win-icon">🪙</div>

          <div className="qr-coin-success-tag">REWARD UNLOCKED</div>
          <h2>Aapne Coins Jeete!</h2>
          <div className="qr-coin-win-number">+{wonCoins}</div>
          <p className="qr-coin-win-subtitle">Coins successfully earned</p>
        </div>
      </div>,
      document.body
    );
  };

  const renderCoinClaimPopup = () => {
    if (!showCoinClaimPopup || typeof document === 'undefined') return null;

    return createPortal(
      <div
        className="qr-coin-claim-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Claim coins in Food Radar"
        onClick={() => setShowCoinClaimPopup(false)}
      >
        <div
          className="qr-coin-claim-popup"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="qr-coin-popup-close"
            onClick={() => setShowCoinClaimPopup(false)}
            aria-label="Close"
          >
            ×
          </button>

          <div className="qr-coin-popup-brand">VAKRA FOOD RADAR</div>

          <div className="qr-coin-popup-icon-wrap">
            <div className="qr-coin-popup-icon">🪙</div>
          </div>

          <h3>Claim Your Coins</h3>
          <p>
            Apne earned coins ko claim aur use karne ke liye Food Radar app
            install/open karke isi mobile number se login karein.
          </p>

          {wonCoins > 0 ? (
            <div className="qr-coin-popup-balance">
              <span>Your reward</span>
              <strong>{wonCoins} Coins</strong>
            </div>
          ) : null}

          <button
            type="button"
            className="qr-coin-install-btn"
            onClick={() => {
              setShowCoinClaimPopup(false);
              window.location.href = '/food-radar';
            }}
          >
            <span>📱</span>
            Install / Open Food Radar
          </button>

          <button
            type="button"
            className="qr-coin-later-btn"
            onClick={() => setShowCoinClaimPopup(false)}
          >
            Maybe Later
          </button>

          <div className="qr-coin-popup-safe-note">
            Same mobile number se login karein
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderMinOrderPopup = () => {
    if (!minOrderPopup) return null;

    return (
      <div className="fastfood-minorder-popup-overlay" role="dialog" aria-modal="true">
        <div className="fastfood-minorder-popup-card" key={minOrderPopup.key || 'min-order-popup'}>
          <button
            type="button"
            className="fastfood-minorder-popup-close"
            onClick={() => setMinOrderPopup(null)}
            aria-label="Close minimum order popup"
          >
            ×
          </button>

          <div className="fastfood-minorder-popup-icon">🛒</div>
          <h3>Minimum order required</h3>
          <p>
            Review order open karne ke liye minimum order amount {formatMoney(minOrderPopup.minOrder)} hona chahiye.
          </p>

          <div className="fastfood-minorder-popup-grid">
            <div>
              <span>Current cart</span>
              <strong>{formatMoney(minOrderPopup.currentTotal)}</strong>
            </div>
            <div>
              <span>Add more</span>
              <strong>{formatMoney(minOrderPopup.required)}</strong>
            </div>
          </div>

          <button type="button" className="grocery-action-button" onClick={() => setMinOrderPopup(null)}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="grocery-qr-wrapper">
        <div className="grocery-qr-container">
          <div className="grocery-loading-card">
            <h2>Loading fast food QR...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (qrAccess.checked && !qrAccess.isActive && !notificationTrackRequested) {
    return renderInactiveQrPage();
  }

  return (
    <div
      className="grocery-qr-wrapper fastfood-qr-wrapper"
      style={{
        backgroundImage: shopImage
          ? `linear-gradient(rgba(247,244,239,.94), rgba(247,244,239,.97)), url(${normalizeImage(shopImage)})`
          : undefined,
      }}
    >
      {(paymentLoading || isPaymentVerifying) ? (
        <div className="payment-overlay">
          <div className="payment-overlay-card">
            <div className="payment-spinner" />
            <h3>Processing Payment</h3>
            <p>{paymentStatusMessage || 'Please wait while we confirm your payment.'}</p>
            <small>Page band mat kijiye. Verification complete hote hi next step khul jayega.</small>
          </div>
        </div>
      ) : null}

      <h1 className="grocery-shop-name">{shopName}</h1>
      {renderMessageBlock()}

      {step === STEP_LANDING && renderLandingStep()}
      {step === STEP_CUSTOMER && renderCustomerStep()}
      {step === STEP_PRODUCTS && renderProductsStep()}
      {step === STEP_REVIEW && renderReviewStep()}
      {step === STEP_PAYMENT && renderPaymentStep()}
      {step === STEP_SUCCESS && renderSuccessStep()}
      {step === STEP_TRACK && renderTrackStep()}

      {renderCoinWinPopup()}
      {renderCoinClaimPopup()}
      {renderReviewNotificationPrompt()}
      {renderMinOrderPopup()}
      {renderVendorContactPopup()}
    </div>
  );
};

export default StandardQrForm;
